import React, { useState, useEffect, useRef, forwardRef } from 'react';
import { 
  LayoutDashboard, Wallet, ArrowDownCircle, ArrowUpCircle, PieChart, Users, Settings, 
  Plus, FileText, Menu, X, LogOut, CheckCircle, Shield, Building, Loader, AlertCircle, Briefcase, ArrowLeft, 
  Trash2, Download, Printer, FileBadge, Paperclip, TrendingUp, AlertTriangle, Activity, Calendar, Clock,
  Pencil, Target, Key, Database, Save, HelpCircle, PauseCircle, PlayCircle, UserPlus, Lock
} from 'lucide-react';

// --- IMPORTS DE FIREBASE ---
import { initializeApp, deleteApp } from "firebase/app";
import { 
  getFirestore, collection, query, where, getDocs, doc, setDoc, updateDoc, addDoc, deleteDoc, onSnapshot, serverTimestamp, orderBy 
} from "firebase/firestore";
import { 
  getAuth, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, onAuthStateChanged, signInWithCustomToken, signInAnonymously, sendPasswordResetEmail
} from "firebase/auth";

// --- CONFIGURACIÓN ---
const firebaseConfig = {
  apiKey: "AIzaSyDJbyBdr5ANN9v7XhXAjRRj2UgveOYDZqM", 
  authDomain: "plataforma-financiera-4451b.firebaseapp.com",
  projectId: "plataforma-financiera-4451b",
  storageBucket: "plataforma-financiera-4451b.firebasestorage.app",
  messagingSenderId: "247701395874",
  appId: "1:247701395874:web:322a72e415efd49a7a7afb"
};

// Evitar reinicialización si ya existe
let app;
try {
    app = initializeApp(firebaseConfig);
} catch (e) {
    // Si ya está inicializada, ignorar error o tomar la instancia existente
}

const db = getFirestore(app);
const auth = getAuth(app);

// --- DICCIONARIO DE TRADUCCIONES (ES/EN) ---
const DICTIONARY = {
  es: {
    reportTitle: "Informe Financiero Oficial",
    period: "Período",
    generated: "Generado",
    execSummary: "Resumen Ejecutivo",
    prevBalance: "Saldo Anterior",
    income: "Entradas",
    expense: "Salidas",
    finalBalance: "Saldo Final",
    details: "Detalle de Movimientos",
    date: "Fecha",
    desc: "Descripción",
    dept: "Departamento",
    noMovements: "No hubo movimientos en este período.",
    totals: "Totales del Período",
    footer: "Documento generado oficialmente por plataforma Mayordomo Fiel",
    certTitle: "Constancia de Donativo",
    fiscalYear: "Año Fiscal",
    issuedTo: "Emitido A (Donante)",
    letterBody1: "Estamos profundamente agradecidos por su fiel colaboración y generoso apoyo financiero al ministerio de",
    letterBody2: "Esta carta sirve como su recibo oficial para fines de deducción de impuestos para el año",
    verse: '"Traed todos los diezmos al alfolí... Probadme ahora en esto", dice Jehová de los ejércitos.',
    verseRef: "— Malaquías 3:10",
    fund: "Fondo / Concepto",
    amount: "Monto",
    totalContrib: "Contribuciones Totales",
    disclaimer: "No se proporcionaron bienes o servicios a cambio de estas contribuciones.",
    sigTitles: {
      "Senior Pastor": "Pastor Principal",
      "Assistant Pastor": "Pastor Asistente",
      "Treasurer": "Tesorero(a)",
      "Secretary": "Secretario(a)",
      "Deacon": "Diácono",
      "Trustee": "Fideicomisario",
      "Church Administrator": "Administrador(a) de la Iglesia",
      "Finance Committee Head": "Líder de Finanzas"
    }
  },
  en: {
    reportTitle: "Official Financial Report",
    period: "Period",
    generated: "Generated",
    execSummary: "Executive Summary",
    prevBalance: "Previous Balance",
    income: "Income",
    expense: "Expenses",
    finalBalance: "Final Balance",
    details: "Transaction Details",
    date: "Date",
    desc: "Description",
    dept: "Department",
    noMovements: "No transactions in this period.",
    totals: "Period Totals",
    footer: "Officially generated via Mayordomo Fiel Platform",
    certTitle: "Official Contribution Statement",
    fiscalYear: "Fiscal Year",
    issuedTo: "Issued To (Donor)",
    letterBody1: "We are profoundly grateful for your faithful partnership and generous financial support of the ministry at",
    letterBody2: "This letter serves as your official receipt for tax deduction purposes for",
    verse: '"Bring the whole tithe into the storehouse... Test me in this," says the Lord Almighty.',
    verseRef: "— Malachi 3:10",
    fund: "Fund",
    amount: "Amount",
    totalContrib: "Total Contributions",
    disclaimer: "No goods or services were provided in exchange for these contributions.",
    sigTitles: {
      "Senior Pastor": "Senior Pastor",
      "Assistant Pastor": "Assistant Pastor",
      "Treasurer": "Treasurer",
      "Secretary": "Secretary",
      "Deacon": "Deacon",
      "Trustee": "Trustee",
      "Church Administrator": "Church Administrator",
      "Finance Committee Head": "Finance Committee Head"
    }
  }
};

const getSigTitle = (title, lang = 'es') => {
  if (DICTIONARY[lang] && DICTIONARY[lang].sigTitles && DICTIONARY[lang].sigTitles[title]) {
    return DICTIONARY[lang].sigTitles[title];
  }
  return title;
};

// --- UTILIDADES ---
const formatMoney = (amount, currency = 'MXN') => {
  const value = Number(amount) || 0;
  if (currency === 'USD') {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  }
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(value);
};

const formatFirestoreDate = (timestamp) => {
  if (!timestamp) return '-';
  try {
    if (typeof timestamp.toDate === 'function') {
      return timestamp.toDate().toLocaleDateString() + ' ' + timestamp.toDate().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
    }
    if (timestamp instanceof Date) {
      return timestamp.toLocaleDateString() + ' ' + timestamp.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
    }
    return String(timestamp);
  } catch (e) { 
    return '-'; 
  }
};

const logAudit = async (tenantId, user, action, details) => {
  try {
    await addDoc(collection(db, "audit_logs"), {
      tenantId, 
      userEmail: user.email, 
      userId: user.uid, 
      action, 
      details: String(details), 
      timestamp: serverTimestamp() 
    });
  } catch (e) { 
    console.warn("Audit log error:", e); 
  }
};

const exportToCSV = (data, filename) => {
  if (!data || !data.length) { 
    window.alert("No hay datos para exportar."); 
    return; 
  }
  const headers = Object.keys(data[0]).join(",");
  const rows = data.map(obj => 
    Object.values(obj).map(val => {
      const stringVal = val === null || val === undefined ? '' : String(val);
      return `"${stringVal.replace(/"/g, '""')}"`;
    }).join(",")
  );
  const bom = "\uFEFF";
  const csv = bom + headers + "\n" + rows.join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
  document.body.appendChild(link); 
  link.click(); 
  document.body.removeChild(link);
};

// --- COMPONENTES UI BÁSICOS ---
const BrandLogo = ({ className = "w-12 h-12" }) => (
  <img 
    src="https://static.wixstatic.com/media/bcb13e_ee3c4ba2567445a48ec97d0793b7e1a9~mv2.png"
    alt="Mayordomo Fiel"
    className={`${className} object-contain`} 
  />
);

const ChurchLogo = ({ url, className = "w-12 h-12" }) => {
  if (url) return <img src={url} alt="Logo" className={`${className} object-contain`} />;
  return <Building className={`${className} text-white opacity-50`} />;
};

const Button = ({ children, variant = 'primary', onClick, className = '', color = '#b4111a', disabled = false, type = 'button' }) => {
  const styles = {
    primary: `text-white hover:shadow-lg hover:brightness-110`,
    secondary: "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50",
    ghost: "bg-transparent text-gray-600 hover:bg-gray-100",
    dark: "bg-slate-800 text-white hover:bg-slate-700",
    white: "bg-white text-[#b4111a] hover:bg-gray-50 shadow-lg"
  };
  return (
    <button 
      type={type} 
      onClick={disabled ? undefined : onClick} 
      disabled={disabled} 
      className={`px-4 py-3 rounded-lg text-sm font-semibold transition-all flex items-center justify-center disabled:opacity-50 ${styles[variant]} ${className}`} 
      style={variant === 'primary' && !disabled ? { backgroundColor: color } : {}}
    >
      {children}
    </button>
  );
};

// --- MODALES DE IMPRESIÓN ---

const MonthlyReportModal = ({ isOpen, onClose, data, tenant, periodName }) => {
  const printRef = useRef();

  if (!isOpen || !data) return null;

  const lang = tenant?.config?.language || 'es';
  const currency = tenant?.config?.currency || 'MXN';
  const texts = DICTIONARY[lang];
  const signatures = tenant?.config?.signatures || [];

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>${texts.reportTitle} - ${periodName}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=swap');
            body { font-family: 'Roboto', sans-serif; padding: 20px; font-size: 10pt; color: #1f2937; }
            .header-table { width: 100%; border-bottom: 2px solid #111827; padding-bottom: 10px; margin-bottom: 15px; }
            .logo-img { max-height: 50px; width: auto; object-fit: contain; } 
            .org-name { font-size: 16pt; font-weight: bold; text-transform: uppercase; margin: 0; line-height: 1.2; }
            .report-name { font-size: 10pt; color: #6b7280; margin: 0; }
            
            .summary-box { display: flex; gap: 10px; margin-bottom: 20px; }
            .kpi-card { flex: 1; border: 1px solid #e5e7eb; padding: 8px; border-radius: 4px; background: #f9fafb; }
            .kpi-label { font-size: 8pt; text-transform: uppercase; color: #6b7280; font-weight: bold; margin-bottom: 4px; }
            .kpi-value { font-size: 12pt; font-weight: bold; color: #111827; }
            
            .data-table { width: 100%; border-collapse: collapse; font-size: 9pt; }
            .data-table th { background-color: #f3f4f6; text-align: left; padding: 6px; border-bottom: 1px solid #d1d5db; font-weight: bold; text-transform: uppercase; font-size: 8pt; }
            .data-table td { padding: 6px; border-bottom: 1px solid #e5e7eb; }
            .data-table tr:nth-child(even) { background-color: #f9fafb; }
            
            .footer { margin-top: 30px; border-top: 1px solid #e5e7eb; padding-top: 10px; font-size: 8pt; color: #9ca3af; text-align: center; }
            
            .signatures-row { margin-top: 40px; display: flex; justify-content: space-around; page-break-inside: avoid; }
            .sig-block { text-align: center; width: 30%; }
            .sig-line { border-top: 1px solid #000; margin-bottom: 5px; height: 1px; width: 100%; display: block; }
            .sig-name { font-weight: bold; font-size: 9pt; text-transform: uppercase; }
            .sig-title { font-size: 8pt; color: #4b5563; text-transform: uppercase; }
          </style>
        </head>
        <body>
          <table class="header-table">
            <tr>
                <td style="width: 80px; vertical-align: middle;">
                    ${tenant?.config?.logoUrl ? `<img src="${tenant.config.logoUrl}" class="logo-img" />` : ''}
                </td>
                <td style="vertical-align: middle;">
                    <h1 class="org-name">${tenant?.name}</h1>
                    <p class="report-name">${texts.reportTitle}</p>
                </td>
                <td style="text-align: right; vertical-align: middle;">
                    <div style="font-weight: bold; font-size: 11pt;">${periodName}</div>
                    <div style="font-size: 8pt; color: #9ca3af;">${texts.generated}: ${new Date().toLocaleDateString()}</div>
                </td>
            </tr>
          </table>

          <div class="summary-box">
             <div class="kpi-card">
                <div class="kpi-label">${texts.prevBalance}</div>
                <div class="kpi-value">${formatMoney(data.startBalance, currency)}</div>
             </div>
             <div class="kpi-card" style="border-left: 3px solid #10b981;">
                <div class="kpi-label" style="color: #059669;">${texts.income}</div>
                <div class="kpi-value" style="color: #059669;">+${formatMoney(data.totalIncome, currency)}</div>
             </div>
             <div class="kpi-card" style="border-left: 3px solid #ef4444;">
                <div class="kpi-label" style="color: #dc2626;">${texts.expense}</div>
                <div class="kpi-value" style="color: #dc2626;">-${formatMoney(data.totalExpense, currency)}</div>
             </div>
             <div class="kpi-card" style="background: #eff6ff; border-color: #bfdbfe;">
                <div class="kpi-label" style="color: #1d4ed8;">${texts.finalBalance}</div>
                <div class="kpi-value" style="color: #1e40af;">${formatMoney(data.endBalance, currency)}</div>
             </div>
          </div>

          <h3 style="font-size: 10pt; text-transform: uppercase; border-bottom: 1px solid #d1d5db; padding-bottom: 5px; margin-bottom: 10px;">${texts.details}</h3>
          
          <table class="data-table">
             <thead>
                <tr>
                    <th style="width: 80px;">${texts.date}</th>
                    <th>${texts.desc}</th>
                    <th>${texts.dept}</th>
                    <th style="text-align: right; width: 100px;">${texts.amount}</th>
                </tr>
             </thead>
             <tbody>
                ${data.transactions.length === 0 ? `<tr><td colspan="4" style="text-align: center; color: #9ca3af; padding: 20px;">${texts.noMovements}</td></tr>` : 
                  data.transactions.map(tx => `
                    <tr>
                        <td>${tx.date}</td>
                        <td>${tx.description}</td>
                        <td style="font-size: 8pt; color: #6b7280; text-transform: uppercase;">${tx.departmentName}</td>
                        <td style="text-align: right; font-weight: bold; color: ${tx.type === 'income' ? '#059669' : '#dc2626'};">
                            ${tx.type === 'income' ? '+' : '-'}${formatMoney(tx.amount, currency)}
                        </td>
                    </tr>
                  `).join('')
                }
             </tbody>
          </table>

          ${signatures.length > 0 ? `
            <div class="signatures-row">
                ${signatures.map(sig => `
                    <div class="sig-block">
                        <div class="sig-line"></div>
                        <div class="sig-name">${sig.name}</div>
                        <div class="sig-title">${getSigTitle(sig.title, lang)}</div>
                    </div>
                `).join('')}
            </div>
          ` : ''}

          <div class="footer">
             ${texts.footer}
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => {
        printWindow.focus();
        printWindow.print();
        printWindow.close();
    }, 500);
  };

  return (
    <div id="print-overlay" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-md">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="p-6 text-center">
            <div className="mx-auto w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mb-4">
                <FileText size={32} />
            </div>
            <h3 className="text-xl font-bold mb-2">Reporte Generado</h3>
            <p className="text-gray-500 mb-6 text-sm">El reporte de <strong>{periodName}</strong> está listo para imprimir.</p>
            <div className="flex gap-3 justify-center">
                <Button variant="secondary" onClick={onClose}>Cancelar</Button>
                <Button onClick={handlePrint}><Printer size={16} className="mr-2"/> Imprimir Reporte</Button>
            </div>
        </div>
      </div>
    </div>
  );
};

const CertificateModal = ({ isOpen, onClose, member, totalGiven, tenant, year }) => {
  const printRef = useRef();
  
  if (!isOpen || !member) return null;

  const lang = tenant?.config?.language || 'es';
  const currency = tenant?.config?.currency || 'MXN';
  const texts = DICTIONARY[lang];
  const signatures = tenant?.config?.signatures?.filter(s => {
      const t = s.title.toLowerCase();
      return t.includes('treasurer') || t.includes('pastor') || t.includes('finan') || t.includes('admin') || t.includes('sec');
  }).slice(0, 3) || [];

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>${texts.certTitle}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=Lato:wght@300;400;700&display=swap');
            body { margin: 0; padding: 0; background: #fff; font-family: 'Lato', sans-serif; }
            .page-container { 
                padding: 40px; 
                width: 100%; 
                height: 100vh; 
                box-sizing: border-box; 
                display: flex; 
                flex-direction: column;
                justify-content: center;
            }
            .certificate-border {
                border: 5px double #333;
                padding: 40px;
                height: 90%;
                position: relative;
                display: flex;
                flex-direction: column;
                justify-content: space-between;
                text-align: center;
            }
            .corner-decoration {
                position: absolute;
                width: 30px;
                height: 30px;
                border: 2px solid #b4111a;
            }
            .top-left { top: 10px; left: 10px; border-right: none; border-bottom: none; }
            .top-right { top: 10px; right: 10px; border-left: none; border-bottom: none; }
            .bottom-left { bottom: 10px; left: 10px; border-right: none; border-top: none; }
            .bottom-right { bottom: 10px; right: 10px; border-left: none; border-top: none; }

            .header-logo { max-height: 80px; width: auto; margin-bottom: 10px; object-fit: contain; }
            .church-name { font-family: 'Playfair Display', serif; font-size: 24pt; font-weight: bold; color: #111827; text-transform: uppercase; margin: 0; }
            .doc-title { font-size: 14pt; letter-spacing: 3px; text-transform: uppercase; color: #6b7280; margin-top: 5px; margin-bottom: 30px; border-bottom: 1px solid #e5e7eb; display: inline-block; padding-bottom: 5px; }
            
            .main-text { font-size: 12pt; line-height: 1.6; color: #374151; max-width: 80%; margin: 0 auto; }
            
            .recipient-box { margin: 30px auto; width: 70%; border: 1px solid #d1d5db; padding: 20px; background: #f9fafb; }
            .recipient-label { font-size: 9pt; text-transform: uppercase; color: #6b7280; letter-spacing: 1px; margin-bottom: 5px; }
            .recipient-name { font-family: 'Playfair Display', serif; font-size: 22pt; font-weight: bold; color: #1f2937; margin-bottom: 15px; }
            .amount-section { border-top: 1px dashed #d1d5db; paddingTop: 15px; margin-top: 15px; }
            .total-amount { font-size: 18pt; font-weight: bold; color: #000; }
            
            .verse-box { font-style: italic; color: #4b5563; font-size: 10pt; margin: 20px auto; max-width: 70%; }
            
            .signatures-grid { display: flex; justify-content: center; gap: 40px; margin-top: 40px; padding: 0 40px; }
            .sig-item { flex: 1; text-align: center; max-width: 200px; }
            .sig-line { border-top: 1px solid #000; margin-bottom: 8px; }
            .sig-name { font-weight: bold; font-size: 10pt; text-transform: uppercase; }
            .sig-role { font-size: 8pt; color: #6b7280; text-transform: uppercase; }
            
            .footer-info { font-size: 8pt; color: #9ca3af; margin-top: 20px; }
          </style>
        </head>
        <body>
          <div class="page-container">
            <div class="certificate-border">
                <div class="corner-decoration top-left"></div>
                <div class="corner-decoration top-right"></div>
                <div class="corner-decoration bottom-left"></div>
                <div class="corner-decoration bottom-right"></div>

                <div>
                    ${tenant?.config?.logoUrl ? `<img src="${tenant.config.logoUrl}" class="header-logo" />` : ''}
                    <h1 class="church-name">${tenant?.name}</h1>
                    <div class="doc-title">${texts.certTitle} | ${year}</div>
                </div>

                <div class="main-text">
                    ${texts.letterBody1} <strong>${tenant?.name}</strong>.
                </div>

                <div class="recipient-box">
                    <div class="recipient-label">${texts.issuedTo}</div>
                    <div class="recipient-name">${member.fullName}</div>
                    
                    <div class="amount-section">
                        <div class="recipient-label">${texts.totalContrib}</div>
                        <div class="total-amount">${formatMoney(totalGiven, currency)}</div>
                    </div>
                </div>

                <div class="main-text">
                    ${texts.letterBody2} <strong>${year}</strong>. ${texts.disclaimer}
                </div>

                <div class="verse-box">
                    "${texts.verse}" <br/> <strong>${texts.verseRef}</strong>
                </div>

                <div class="signatures-grid">
                    ${signatures.map(s => `
                        <div class="sig-item">
                            <div class="sig-line"></div>
                            <div class="sig-name">${s.name}</div>
                            <div class="sig-role">${getSigTitle(s.title, lang)}</div>
                        </div>
                    `).join('')}
                </div>

                <div class="footer-info">
                    ID: ${member.id} | Date: ${new Date().toLocaleDateString()}
                </div>
            </div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => {
        printWindow.focus();
        printWindow.print();
        printWindow.close();
    }, 500);
  };

  return (
    <div id="print-overlay" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-md">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="p-6 text-center">
            <div className="mx-auto w-16 h-16 bg-yellow-100 text-yellow-600 rounded-full flex items-center justify-center mb-4">
                <FileBadge size={32} />
            </div>
            <h3 className="text-xl font-bold mb-2">Certificado Listo</h3>
            <p className="text-gray-500 mb-6 text-sm">El certificado para <strong>{member.fullName}</strong> se ha generado correctamente.</p>
            <div className="flex gap-3 justify-center">
                <Button variant="secondary" onClick={onClose}>Cerrar</Button>
                <Button onClick={handlePrint}><Printer size={16} className="mr-2"/> Imprimir Certificado</Button>
            </div>
        </div>
      </div>
    </div>
  );
};


// --- VISTAS PRINCIPALES ---

const ReportsView = ({ tenantId, tenant, user }) => {
  const [loading, setLoading] = useState(false);
  const [month, setMonth] = useState(new Date().getMonth());
  const [year, setYear] = useState(new Date().getFullYear());
  const [reportData, setReportData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const months = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", 
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
  ];

  const generateReport = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "transactions"), where("tenantId", "==", tenantId));
      const snap = await getDocs(q);
      const allTx = snap.docs.map(d => d.data());

      const startDate = new Date(year, month, 1);
      const endDate = new Date(year, month + 1, 0); 
      const startStr = startDate.toISOString().split('T')[0];
      const endStr = endDate.toISOString().split('T')[0];

      let startBalance = 0;
      let totalIncome = 0;
      let totalExpense = 0;
      const periodTx = [];

      allTx.forEach(tx => {
         const txDate = tx.date; 
         const val = Number(tx.amount);
         
         if (txDate < startStr) {
           if (tx.type === 'income') startBalance += val;
           else startBalance -= val;
         } else if (txDate >= startStr && txDate <= endStr) {
           if (tx.type === 'income') totalIncome += val;
           else totalExpense += val;
           periodTx.push(tx);
         }
      });

      periodTx.sort((a, b) => new Date(a.date) - new Date(b.date));

      setReportData({
        startBalance,
        totalIncome,
        totalExpense,
        endBalance: startBalance + totalIncome - totalExpense,
        transactions: periodTx
      });
      setIsModalOpen(true);
      await logAudit(tenantId, user, "Generar Reporte", `${months[month]} ${year}`);

    } catch (e) {
      console.error(e);
      window.alert("Error generando reporte: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto animate-fadeIn">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Centro de Reportes</h2>
        <p className="text-gray-500 text-sm">Genere informes oficiales mensuales para su archivo y presentación.</p>
      </div>

      <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row items-end gap-6">
        <div className="flex-1 w-full">
           <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Mes del Reporte</label>
           <div className="grid grid-cols-2 gap-4">
              <select 
                value={month} 
                onChange={(e) => setMonth(parseInt(e.target.value))}
                className="block w-full border border-gray-300 rounded-lg p-3 bg-white focus:ring-2 focus:ring-blue-500 outline-none"
              >
                {months.map((m, i) => <option key={i} value={i}>{m}</option>)}
              </select>
              <input 
                type="number" 
                value={year} 
                onChange={(e) => setYear(parseInt(e.target.value))}
                className="block w-full border border-gray-300 rounded-lg p-3 bg-white focus:ring-2 focus:ring-blue-500 outline-none"
              />
           </div>
        </div>
        
        <Button 
          onClick={generateReport} 
          disabled={loading}
          className="w-full md:w-auto h-[50px] px-8 shadow-xl"
        >
          {loading ? <Loader className="animate-spin" /> : <><FileText size={18} className="mr-2"/> Generar Reporte Oficial</>}
        </Button>
      </div>
      
      <MonthlyReportModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        data={reportData} 
        tenant={tenant}
        periodName={`${months[month]} ${year}`}
      />
    </div>
  );
};

const ReceiptViewer = ({ isOpen, onClose, imgUrl }) => {
  if (!isOpen || !imgUrl) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/90" onClick={onClose}>
      <div className="relative max-w-3xl max-h-full">
        <button className="absolute -top-10 right-0 text-white"><X size={32}/></button>
        <img src={imgUrl} alt="Recibo" className="max-h-[85vh] rounded shadow-2xl" onClick={e => e.stopPropagation()} />
      </div>
    </div>
  );
};

const PricingModal = ({ isOpen, onClose, user, tenantId, tenantConfig }) => {
  const [activationKey, setActivationKey] = useState('');
  const [activating, setActivating] = useState(false);
  const [prices, setPrices] = useState({ small: 25, medium: 50, large: 99 });
  const [links, setLinks] = useState({ small: '#', medium: '#', large: '#' });
  
  const currency = tenantConfig?.currency || 'MXN';
  const isMXN = currency === 'MXN';
  const currencySymbol = isMXN ? 'MXN $' : 'USD $';

  useEffect(() => {
    if (!isOpen) return;
    const unsub = onSnapshot(doc(db, "system", "config"), (doc) => {
        if (doc.exists()) {
            const data = doc.data();
            if (isMXN && data.prices_mxn) setPrices(data.prices_mxn);
            else if (!isMXN && data.prices_usd) setPrices(data.prices_usd);
            else if (data.prices) setPrices(data.prices);

            if (data.links) setLinks(data.links);
        }
    });
    return () => unsub();
  }, [isOpen, isMXN]);

  const handleRedeem = async () => {
    if (!activationKey) return;
    setActivating(true);
    try {
        const q = query(collection(db, "licenses"), where("key", "==", activationKey), where("status", "==", "active"));
        const snapshot = await getDocs(q);
        
        if (!snapshot.empty) {
            const licenseDoc = snapshot.docs[0];
            await updateDoc(doc(db, "tenants", tenantId), { plan: 'premium', trialEndDate: null });
            await updateDoc(doc(db, "licenses", licenseDoc.id), { status: 'redeemed', usedBy: tenantId, usedAt: new Date() });
            
            window.alert("¡Licencia Activada Correctamente! Bienvenido a Premium.");
            window.location.reload();
        } else {
             window.alert("Clave inválida o ya utilizada.");
        }
    } catch (e) {
        console.error(e);
        window.alert("Error al canjear: " + e.message);
    } finally {
        setActivating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/80 backdrop-blur-sm">
        <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-5xl bg-white rounded-2xl shadow-2xl overflow-hidden my-8">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><X size={24}/></button>
                <div className="p-8 text-center bg-gray-50 border-b">
                <h2 className="text-3xl font-extrabold text-gray-900">Planes diseñados para tu crecimiento</h2>
                <p className="text-gray-500 mt-2">Moneda detectada: {currency}. Elige el plan que mejor se adapte.</p>
                </div>
                <div className="p-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="border border-gray-200 rounded-xl p-6 hover:shadow-xl transition-shadow flex flex-col">
                    <h3 className="text-lg font-bold text-gray-500 uppercase">Pequeña</h3>
                    <div className="my-4"><span className="text-4xl font-extrabold text-gray-900">{currencySymbol}{prices.small}</span><span className="text-gray-500">/mes</span></div>
                    <ul className="space-y-3 mb-8 flex-1 text-sm text-gray-600">
                    <li className="flex items-center"><CheckCircle size={16} className="text-green-500 mr-2"/> Hasta 50 miembros</li>
                    <li className="flex items-center"><CheckCircle size={16} className="text-green-500 mr-2"/> 1 Usuario Admin</li>
                    <li className="flex items-center text-sm"><CheckCircle size={16} className="text-green-500 mr-2"/> Certificados PDF</li>
                    </ul>
                    <a href={links.small || '#'} target="_blank" rel="noreferrer" className="block w-full py-3 text-center rounded-lg border border-[#b4111a] text-[#b4111a] font-bold">Comprar</a>
                </div>
                <div className="border-2 border-[#b4111a] rounded-xl p-6 shadow-xl relative flex flex-col transform scale-105 bg-white">
                    <div className="absolute top-0 right-0 bg-[#b4111a] text-white text-xs font-bold px-3 py-1 rounded-bl-lg">POPULAR</div>
                    <h3 className="text-lg font-bold text-[#b4111a] uppercase">Mediana</h3>
                    <div className="my-4"><span className="text-4xl font-extrabold text-gray-900">{currencySymbol}{prices.medium}</span><span className="text-gray-500">/mes</span></div>
                    <ul className="space-y-3 mb-8 flex-1 text-sm text-gray-600">
                    <li className="flex items-center"><CheckCircle size={16} className="text-green-500 mr-2"/> Hasta 250 miembros</li>
                    <li className="flex items-center"><CheckCircle size={16} className="text-green-500 mr-2"/> 3 Usuarios Admin</li>
                    </ul>
                    <a href={links.medium || '#'} target="_blank" rel="noreferrer" className="block w-full py-3 text-center rounded-lg bg-[#b4111a] text-white font-bold">Comprar</a>
                </div>
                <div className="border border-gray-200 rounded-xl p-6 hover:shadow-xl transition-shadow flex flex-col">
                    <h3 className="text-lg font-bold text-gray-500 uppercase">Bundle</h3>
                    <div className="my-4"><span className="text-4xl font-extrabold text-gray-900">{currencySymbol}{prices.large}</span><span className="text-gray-500">/mes</span></div>
                    <ul className="space-y-3 mb-8 flex-1 text-sm text-gray-600">
                    <li className="flex items-center"><CheckCircle size={16} className="text-green-500 mr-2"/> Todo Ilimitado</li>
                    <li className="flex items-center"><CheckCircle size={16} className="text-green-500 mr-2"/> Recursos Devocionales</li>
                    <li className="flex items-center"><CheckCircle size={16} className="text-green-500 mr-2"/> Bolsa de Café de 12oz al mes (solo en USA)</li>
                    </ul>
                    <a href={links.large || '#'} target="_blank" rel="noreferrer" className="block w-full py-3 text-center rounded-lg border border-[#b4111a] text-[#b4111a] font-bold">Comprar</a>
                </div>
                </div>
                <div className="bg-gray-900 text-white p-6 flex flex-col md:flex-row items-center justify-between">
                <div className="mb-4 md:mb-0">
                    <h4 className="font-bold flex items-center"><Key size={18} className="mr-2"/> Activar Licencia</h4>
                    <p className="text-sm text-gray-400">Introduce la clave enviada a tu correo.</p>
                </div>
                <div className="flex w-full md:w-auto gap-2">
                    <input type="text" placeholder="XXXX-XXXX-XXXX" className="bg-gray-800 border border-gray-700 text-white px-4 py-2 rounded-lg outline-none uppercase w-full md:w-64" value={activationKey} onChange={e => setActivationKey(e.target.value)} />
                    <button onClick={handleRedeem} disabled={activating} className="bg-green-600 hover:bg-green-500 px-6 py-2 rounded-lg font-bold disabled:opacity-50">
                    {activating ? '...' : 'Canjear'}
                    </button>
                </div>
                </div>
            </div>
        </div>
    </div>
  );
};

const TransactionModal = ({ isOpen, onClose, tenantId, themeColor, user, initialData }) => {
  const [type, setType] = useState('income'); 
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [departments, setDepartments] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedDept, setSelectedDept] = useState('');
  const [selectedMember, setSelectedMember] = useState(''); 
  const [receiptFile, setReceiptFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !tenantId) return;
    const load = async () => {
      const dS = await getDocs(query(collection(db, "departments"), where("tenantId", "==", tenantId)));
      setDepartments(dS.docs.map(d => ({id: d.id, ...d.data()})));
      const mS = await getDocs(query(collection(db, "members"), where("tenantId", "==", tenantId)));
      setMembers(mS.docs.map(d => ({id: d.id, ...d.data()})));
    };
    load();
  }, [isOpen, tenantId]);

  useEffect(() => {
    if (initialData) {
      setType(initialData.type);
      setAmount(initialData.amount);
      setDescription(initialData.description);
      setDate(initialData.date);
      setSelectedDept(initialData.departmentId || '');
      setSelectedMember(initialData.memberId || '');
      setReceiptFile(initialData.receiptBase64 || null);
    } else {
      setAmount(''); setDescription(''); setSelectedMember(''); setSelectedDept(''); setReceiptFile(null); setType('income');
    }
  }, [initialData, isOpen]);

  const handleFileChange = (e) => { 
    const file = e.target.files[0]; 
    if (file && file.size < 500000) { 
      const reader = new FileReader(); 
      reader.onloadend = () => setReceiptFile(reader.result); 
      reader.readAsDataURL(file); 
    } else if(file) window.alert("Max 500KB"); 
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); 
    setSaving(true); 
    try {
      const mName = members.find(m => m.id === selectedMember)?.fullName || null;
      const dName = departments.find(d => d.id === selectedDept)?.name || 'General';
      const data = { 
        tenantId, type, amount: parseFloat(amount), description, date, 
        departmentId: selectedDept || null, departmentName: dName, 
        memberId: selectedMember || null, memberName: mName, 
        receiptBase64: receiptFile, createdAt: new Date() 
      };
      
      if (initialData) {
        const txRef = doc(db, "transactions", initialData.id);
        await updateDoc(txRef, { ...data, reconciled: initialData.reconciled });
        await logAudit(tenantId, user, "Editar Transacción", `ID: ${initialData.id} -> ${amount}`);
      } else {
        await addDoc(collection(db, "transactions"), { ...data, reconciled: false });
        await logAudit(tenantId, user, "Crear Transacción", `${type}: ${amount}`);
      }
      onClose();
    } catch (e) { window.alert("Error: " + e.message); } finally { setSaving(false); }
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="px-6 py-4 border-b flex justify-between items-center"><h3 className="font-bold">{initialData ? 'Editar' : 'Nuevo'}</h3><button onClick={onClose}><X/></button></div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="flex bg-gray-100 p-1 rounded-lg">
             <button type="button" onClick={() => setType('income')} className={`flex-1 py-2 text-sm font-medium rounded-md ${type === 'income' ? 'bg-white shadow text-green-700' : 'text-gray-500'}`}>Ingreso</button>
             <button type="button" onClick={() => setType('expense')} className={`flex-1 py-2 text-sm font-medium rounded-md ${type === 'expense' ? 'bg-white shadow text-red-700' : 'text-gray-500'}`}>Gasto</button>
          </div>
          <div className="grid grid-cols-2 gap-4">
             <div><label className="text-xs font-bold text-gray-500">Monto</label><input type="number" className="w-full border rounded px-3 py-2" value={amount} onChange={e => setAmount(e.target.value)} required /></div>
             <div><label className="text-xs font-bold text-gray-500">Fecha</label><input type="date" className="w-full border rounded px-3 py-2" value={date} onChange={e => setDate(e.target.value)} required /></div>
          </div>
          <div><label className="text-xs font-bold text-gray-500">Descripción</label><input type="text" className="w-full border rounded px-3 py-2" value={description} onChange={e => setDescription(e.target.value)} required /></div>
          <div>
             <label className="text-xs font-bold text-gray-500">Departamento</label>
             <select className="w-full border rounded px-3 py-2 bg-white" value={selectedDept} onChange={e => setSelectedDept(e.target.value)} required>
               <option value="">Seleccione...</option>
               {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
             </select>
          </div>
          {type === 'income' && (
             <div>
                <label className="text-xs font-bold text-gray-500">Miembro</label>
                <select className="w-full border rounded px-3 py-2 bg-white" value={selectedMember} onChange={e => setSelectedMember(e.target.value)}>
                   <option value="">Anónimo</option>
                   {members.map(m => <option key={m.id} value={m.id}>{m.fullName}</option>)}
                </select>
             </div>
          )}
          <div className="border-2 border-dashed rounded p-4 text-center">
             <label className="cursor-pointer block">
                <span className="text-sm text-gray-600">{receiptFile ? "Imagen Cargada" : "Adjuntar Recibo"}</span>
                <input type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
             </label>
          </div>
          <Button type="submit" className="w-full" color={themeColor} disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
        </form>
      </div>
    </div>
  );
};

const TransactionsView = ({ tenantId, type, themeColor, user, onEdit, currency }) => {
  const [txs, setTxs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [viewReceipt, setViewReceipt] = useState(null);

  useEffect(() => {
    const q = query(collection(db, "transactions"), where("tenantId", "==", tenantId), where("type", "==", type));
    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map(d => ({id: d.id, ...d.data()}));
      data.sort((a, b) => new Date(b.date) - new Date(a.date));
      setTxs(data);
      const sum = data.reduce((acc, curr) => acc + Number(curr.amount), 0);
      setTotal(sum);
      setLoading(false);
    });
    return () => unsub();
  }, [tenantId, type]);

  const handleDelete = async (id) => {
      if(window.confirm("¿Estás seguro de eliminar este registro permanentemente?")) {
          try {
            await deleteDoc(doc(db, "transactions", id));
            await logAudit(tenantId, user, "Borrar Transacción", id);
          } catch (e) {
            console.error(e);
            window.alert("Error al eliminar: " + e.message);
          }
      }
  }

  // --- PREPARAR DATOS PARA EXPORTACIÓN LIMPIA ---
  const handleExport = () => {
    const cleanData = txs.map(tx => ({
      "Fecha": tx.date,
      "Descripción": tx.description,
      "Departamento": tx.departmentName || '-',
      "Miembro": tx.memberName || (type === 'income' ? 'Anónimo' : '-'),
      "Monto": formatMoney(tx.amount, currency),
      "Usuario": user.email
    }));
    exportToCSV(cleanData, type === 'income' ? "Ingresos" : "Gastos");
  };

  const isIncome = type === 'income';
  const colorClass = isIncome ? 'text-green-700' : 'text-red-700';
  const bgClass = isIncome ? 'bg-green-50' : 'bg-red-50';
  const icon = isIncome ? <ArrowDownCircle size={24} className="text-green-600" /> : <ArrowUpCircle size={24} className="text-red-600" />;

  return (
    <div className="max-w-6xl mx-auto animate-fadeIn">
      <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">{isIncome ? 'Detalle de Entradas' : 'Detalle de Salidas'}</h2>
          <Button variant="secondary" onClick={handleExport} className="text-xs">
             <Download size={14} className="mr-2"/> Exportar CSV
          </Button>
      </div>
      <div className={`p-6 rounded-xl shadow-sm border border-gray-200 mb-8 bg-white`}>
          <div className="flex items-center justify-between">
              <div>
                  <p className="text-gray-500 text-sm font-bold uppercase mb-1">Total Registrado</p>
                  <p className={`text-4xl font-extrabold ${colorClass}`}>{formatMoney(total, currency)}</p>
              </div>
              <div className={`p-4 rounded-full ${bgClass}`}>{icon}</div>
          </div>
      </div>
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* VISTA MÓVIL (TARJETAS) */}
          <div className="md:hidden">
              {loading ? (
                  <div className="p-8 text-center"><Loader className="animate-spin inline text-gray-400"/></div>
              ) : txs.length === 0 ? (
                  <div className="p-8 text-center text-gray-400">No hay registros.</div>
              ) : (
                  <div className="divide-y divide-gray-100">
                      {txs.map(tx => (
                          <div key={tx.id} className="p-4 hover:bg-gray-50 transition-colors">
                              <div className="flex justify-between items-start mb-2">
                                  <div>
                                      <p className="font-bold text-gray-800">{tx.description}</p>
                                      <p className="text-xs text-gray-500">{tx.date}</p>
                                  </div>
                                  <span className={`font-bold ${colorClass}`}>
                                      {formatMoney(tx.amount, currency)}
                                  </span>
                              </div>
                              <div className="flex justify-between items-center mt-3">
                                  <div className="flex gap-2">
                                      <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">{tx.departmentName || '-'}</span>
                                      {isIncome && tx.memberName && <span className="bg-blue-50 text-blue-600 px-2 py-1 rounded text-xs truncate max-w-[100px]">{tx.memberName}</span>}
                                  </div>
                                  <div className="flex space-x-3">
                                      {tx.receiptBase64 && (
                                          <button onClick={() => setViewReceipt(tx.receiptBase64)} className="text-gray-400 hover:text-blue-600" title="Recibo"><Paperclip size={18}/></button>
                                      )}
                                      <button onClick={() => onEdit(tx)} className="text-gray-400 hover:text-green-600" title="Editar"><Pencil size={18}/></button>
                                      <button onClick={() => handleDelete(tx.id)} className="text-red-400 hover:text-red-600" title="Eliminar"><Trash2 size={18}/></button>
                                  </div>
                              </div>
                          </div>
                      ))}
                  </div>
              )}
          </div>

          {/* VISTA ESCRITORIO (TABLA) */}
          <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left text-sm">
                  <thead className="bg-gray-50 text-gray-500 border-b border-gray-100 uppercase text-xs font-bold">
                      <tr>
                          <th className="px-6 py-4">Fecha</th>
                          <th className="px-6 py-4">Descripción</th>
                          <th className="px-6 py-4">Departamento</th>
                          {isIncome && <th className="px-6 py-4">Miembro</th>}
                          <th className="px-6 py-4 text-right">Monto</th>
                          <th className="px-6 py-4 text-center">Acciones</th>
                      </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                      {loading ? (
                          <tr><td colSpan="6" className="p-8 text-center"><Loader className="animate-spin inline"/></td></tr>
                      ) : txs.length === 0 ? (
                          <tr><td colSpan="6" className="p-8 text-center text-gray-400">No hay registros.</td></tr>
                      ) : (
                          txs.map(tx => (
                              <tr key={tx.id} className="hover:bg-gray-50 transition-colors">
                                  <td className="px-6 py-4 text-gray-600 font-medium whitespace-nowrap">{tx.date}</td>
                                  <td className="px-6 py-4 font-semibold text-gray-800">{tx.description}</td>
                                  <td className="px-6 py-4"><span className="bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full text-xs font-bold">{tx.departmentName || '-'}</span></td>
                                  {isIncome && (
                                      <td className="px-6 py-4">
                                          {tx.memberName ? <span className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded">{tx.memberName}</span> : <span className="text-xs text-gray-400">Anónimo</span>}
                                      </td>
                                  )}
                                  <td className={`px-6 py-4 text-right font-bold ${colorClass}`}>{formatMoney(tx.amount, currency)}</td>
                                  <td className="px-6 py-4 flex justify-center space-x-2">
                                      {tx.receiptBase64 && (
                                          <button onClick={() => setViewReceipt(tx.receiptBase64)} className="text-gray-400 hover:text-blue-600 p-1" title="Ver Recibo"><Paperclip size={16}/></button>
                                      )}
                                      <button onClick={() => onEdit(tx)} className="text-gray-400 hover:text-green-600 p-1" title="Editar"><Pencil size={16}/></button>
                                      <button onClick={() => handleDelete(tx.id)} className="text-gray-400 hover:text-red-500 p-1" title="Eliminar"><Trash2 size={16}/></button>
                                  </td>
                              </tr>
                          ))
                      )}
                  </tbody>
              </table>
          </div>
      </div>
      <ReceiptViewer isOpen={!!viewReceipt} onClose={() => setViewReceipt(null)} imgUrl={viewReceipt} />
    </div>
  );
};

const DepartmentsView = ({ tenantId, themeColor, user }) => { 
  const [depts, setDepts] = useState([]); 
  const [newName, setNewName] = useState(''); 
  useEffect(() => { 
    const unsub = onSnapshot(query(collection(db, "departments"), where("tenantId", "==", tenantId)), (snap) => setDepts(snap.docs.map(d => ({id: d.id, ...d.data()})))); 
    return () => unsub(); 
  }, [tenantId]); 
   
  const handleAdd = async (e) => { 
    e.preventDefault(); 
    if (newName.trim()) { 
       await addDoc(collection(db, "departments"), { tenantId, name: newName }); 
       await logAudit(tenantId, user, "Crear Departamento", `Nombre: ${newName}`); 
       setNewName(''); 
    }
  }; 
   
  const handleDelete = async (id, name) => { 
    if (window.confirm("¿Borrar?")) { 
        try {
            await deleteDoc(doc(db, "departments", id)); 
            await logAudit(tenantId, user, "Borrar Departamento", `ID: ${id}, Nombre: ${name}`); 
        } catch (e) {
            console.error(e);
            window.alert("Error al eliminar: " + e.message);
        }
    }
  }; 
   
  return (
    <div className="max-w-4xl mx-auto animate-fadeIn">
      <div className="flex justify-between items-center mb-6"><h2 className="text-2xl font-bold text-gray-800">Departamentos</h2></div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 h-fit">
           <h3 className="font-bold mb-4">Nuevo</h3>
           <form onSubmit={handleAdd}>
             <input className="w-full border rounded px-4 py-2 mb-4" value={newName} onChange={e => setNewName(e.target.value)} placeholder="Nombre" />
             <Button type="submit" className="w-full" color={themeColor}>Agregar</Button>
           </form>
        </div>
        <div className="md:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200">
           <ul className="divide-y">
             {depts.map(d => (
               <li key={d.id} className="p-4 flex justify-between">
                  {d.name} 
                  <button onClick={() => handleDelete(d.id, d.name)} className="text-gray-400 hover:text-red-500"><Trash2 size={18}/></button>
               </li>
             ))}
           </ul>
        </div>
      </div>
    </div>
  ); 
};

const MembersView = ({ tenantId, themeColor, tenantData, user }) => {
  const [members, setMembers] = useState([]);
  const [formData, setFormData] = useState({ fullName: '', email: '' });
  const [cert, setCert] = useState({ open: false, member: null, total: 0, year: new Date().getFullYear() });
  const [yearSelect, setYearSelect] = useState(new Date().getFullYear());

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, "members"), where("tenantId", "==", tenantId)), (snap) => {
      const data = snap.docs.map(d => ({id: d.id, ...d.data()}));
      data.sort((a, b) => a.fullName.localeCompare(b.fullName));
      setMembers(data);
    });
    return () => unsub();
  }, [tenantId]);
  
  const handleAdd = async (e) => { e.preventDefault(); if (formData.fullName.trim()) { await addDoc(collection(db, "members"), { tenantId, ...formData }); await logAudit(tenantId, user, "Crear Miembro", formData.fullName); setFormData({fullName:'', email:''}); }};
  
  const handleCert = async (m) => {
    const yearStr = yearSelect.toString();
    const snap = await getDocs(query(collection(db, "transactions"), where("tenantId", "==", tenantId), where("memberId", "==", m.id), where("type", "==", "income")));
    const total = snap.docs.reduce((acc, d) => d.data().date.startsWith(yearStr) ? acc + d.data().amount : acc, 0);
    setCert({ open: true, member: m, total, year: yearSelect });
  };
  
  const handleDelete = async (id) => { 
      if (window.confirm("¿Borrar?")) { 
          try {
            await deleteDoc(doc(db, "members", id)); 
            await logAudit(tenantId, user, "Borrar Miembro", id); 
          } catch (e) {
            console.error(e);
            window.alert("Error al eliminar: " + e.message);
          }
      }
  };

  // EXPORTACIÓN LIMPIA MIEMBROS
  const handleExportMembers = () => {
    const cleanMembers = members.map(m => ({
        "Nombre Completo": m.fullName,
        "Correo Electrónico": m.email || 'No registrado',
        "Fecha Registro": m.createdAt ? new Date(m.createdAt.toDate ? m.createdAt.toDate() : m.createdAt).toLocaleDateString() : '-'
    }));
    exportToCSV(cleanMembers, "Miembros");
  };

  return (
    <div className="max-w-6xl mx-auto animate-fadeIn">
      <div className="flex justify-between mb-8">
        <h2 className="text-2xl font-bold text-gray-800">Miembros</h2>
        <div className="flex space-x-3 items-center">
            <div className="flex items-center bg-white border rounded-lg px-2 py-1 text-sm"><Calendar size={16} className="mr-2 text-gray-500"/><select value={yearSelect} onChange={(e) => setYearSelect(parseInt(e.target.value))} className="bg-transparent outline-none"><option value={2024}>2024</option><option value={2025}>2025</option><option value={2026}>2026</option></select></div>
            <Button variant="secondary" onClick={handleExportMembers} className="text-xs"><Download size={14} className="mr-2"/> CSV</Button>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 h-fit"><h3 className="font-bold mb-4">Registrar</h3><form onSubmit={handleAdd} className="space-y-4"><input className="w-full border rounded px-4 py-2" value={formData.fullName} onChange={e => setFormData({...formData, fullName: e.target.value})} placeholder="Nombre" required /><input className="w-full border rounded px-4 py-2" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} placeholder="Email" /><Button type="submit" className="w-full" color={themeColor}>Guardar</Button></form></div>
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"><div className="overflow-y-auto max-h-[600px]"><table className="w-full text-left text-sm"><thead className="bg-gray-50"><tr><th className="px-6 py-4">Nombre</th><th className="px-6 py-4 text-right">Acciones</th></tr></thead><tbody className="divide-y">{members.map(m => <tr key={m.id} className="hover:bg-gray-50"><td className="px-6 py-4">{m.fullName}</td><td className="px-6 py-4 text-right"><button onClick={() => handleCert(m)} className="p-2 text-blue-600 hover:bg-blue-50 rounded"><FileBadge size={18}/></button><button onClick={() => handleDelete(m.id)} className="p-2 text-red-500 hover:bg-red-50 rounded"><Trash2 size={18}/></button></td></tr>)}</tbody></table></div></div>
      </div>
      <CertificateModal isOpen={cert.open} onClose={() => setCert({...cert, open: false})} member={cert.member} totalGiven={cert.total} tenant={tenantData} year={cert.year} />
    </div>
  );
};

const TenantSettings = ({ tenant, onUpdateLocal, user }) => { 
  const [config, setConfig] = useState(() => {
    const current = tenant.config || {};
    return {
      ...current,
      primaryColor: current.primaryColor || '#b4111a',
      logoUrl: current.logoUrl || null,
      signatures: Array.isArray(current.signatures) ? current.signatures : [],
      currency: current.currency || '', 
      language: current.language || 'es'
    };
  });
   
  const [activeTab, setActiveTab] = useState('identity'); 
  const [newSigName, setNewSigName] = useState(''); 
  const [newSigTitle, setNewSigTitle] = useState(''); 
  const [loadingBackup, setLoadingBackup] = useState(false);
   
  const signatureTitlesList = [
    { id: "Senior Pastor", labelEs: "Pastor Principal", labelEn: "Senior Pastor" },
    { id: "Assistant Pastor", labelEs: "Pastor Asistente", labelEn: "Assistant Pastor" },
    { id: "Treasurer", labelEs: "Tesorero(a)", labelEn: "Treasurer" },
    { id: "Secretary", labelEs: "Secretario(a)", labelEn: "Secretary" },
    { id: "Deacon", labelEs: "Diácono", labelEn: "Deacon" },
    { id: "Trustee", labelEs: "Fideicomisario", labelEn: "Trustee" },
    { id: "Church Administrator", labelEs: "Administrador(a) de la Iglesia", labelEn: "Church Administrator" },
    { id: "Finance Committee Head", labelEs: "Líder de Finanzas", labelEn: "Finance Committee Head" }
  ];

  const handleSave = async () => { 
    if (!config.currency) {
      window.alert("Por favor selecciona una Moneda Principal");
      return;
    }
    await updateDoc(doc(db, "tenants", tenant.id), { config }); 
    window.alert("¡Configuración Guardada!"); 
    onUpdateLocal({ ...tenant, config }); 
  }; 

  const handleLogo = (e) => { 
    const f = e.target.files[0]; 
    if (f && f.size < 100000) { 
      const r = new FileReader(); 
      r.onloadend = () => setConfig({...config, logoUrl: r.result}); 
      r.readAsDataURL(f); 
    } else window.alert("Logo máximo 100KB"); 
  };

  const handleAddSignature = () => {
    if (!newSigName || !newSigTitle) return;
    const newSigs = [...config.signatures, { name: newSigName, title: newSigTitle }];
    setConfig({ ...config, signatures: newSigs });
    setNewSigName('');
    setNewSigTitle('');
  };

  const handleRemoveSignature = (index) => {
    const newSigs = [...config.signatures];
    newSigs.splice(index, 1);
    setConfig({ ...config, signatures: newSigs });
  };

  // --- FUNCIÓN DE BACKUP COMPLETO ---
  const handleFullBackup = async () => {
    if(!window.confirm("¿Deseas descargar una copia de seguridad completa de todos los datos de tu iglesia? Esto puede tardar unos segundos.")) return;
    
    setLoadingBackup(true);
    try {
        const backupData = {
            metadata: {
                tenantId: tenant.id,
                tenantName: tenant.name,
                generatedAt: new Date().toISOString(),
                exportedBy: user.email
            },
            data: {}
        };

        // Lista de colecciones a respaldar
        const collectionsToBackup = ['transactions', 'members', 'departments', 'budgets', 'audit_logs'];

        for (const colName of collectionsToBackup) {
            const q = query(collection(db, colName), where("tenantId", "==", tenant.id));
            const snapshot = await getDocs(q);
            backupData.data[colName] = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        }

        // Crear blob y descargar
        const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Mayordomo_Backup_${tenant.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        await logAudit(tenant.id, user, "Backup", "Descarga completa de datos");

    } catch (e) {
        console.error(e);
        window.alert("Error generando backup: " + e.message);
    } finally {
        setLoadingBackup(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8 animate-fadeIn text-gray-800 font-sans">
      <div className="flex space-x-6 border-b border-gray-100 pb-2 mb-8 overflow-x-auto">
        <button onClick={() => setActiveTab('identity')} className={`pb-2 px-4 font-black text-xs uppercase tracking-[0.2em] transition-all whitespace-nowrap ${activeTab === 'identity' ? 'text-blue-600 border-b-4 border-blue-600' : 'text-gray-400'}`}>Identidad</button>
        <button onClick={() => setActiveTab('signatures')} className={`pb-2 px-4 font-black text-xs uppercase tracking-[0.2em] transition-all whitespace-nowrap ${activeTab === 'signatures' ? 'text-blue-600 border-b-4 border-blue-600' : 'text-gray-400'}`}>Firmas del Reporte</button>
        <button onClick={() => setActiveTab('data')} className={`pb-2 px-4 font-black text-xs uppercase tracking-[0.2em] transition-all whitespace-nowrap ${activeTab === 'data' ? 'text-blue-600 border-b-4 border-blue-600' : 'text-gray-400'}`}>Datos y Seguridad</button>
      </div>
      <div className="bg-white rounded-[2.5rem] border border-gray-100 p-8 md:p-12 shadow-sm text-gray-700">
        {activeTab === 'identity' && (
          <div className="grid md:grid-cols-2 gap-12 text-gray-800 font-sans">
            <div>
              <label className="block text-[10px] font-black uppercase text-gray-400 mb-6 tracking-widest leading-none">Logo Institucional</label>
              <div className="flex items-center space-x-8">
                <div className="h-28 w-28 border-2 border-dashed rounded-[2rem] flex items-center justify-center bg-gray-50 shadow-inner overflow-hidden">
                  <ChurchLogo url={config.logoUrl} className="w-18 h-18 shadow-none"/>
                </div>
                <label className="cursor-pointer bg-white border border-gray-100 px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-gray-50 shadow-sm transition-all border-none shadow-xl">
                  Cambiar Logo
                  <input type="file" accept="image/*" className="hidden" onChange={handleLogo}/>
                </label>
              </div>
            </div>
            <div className="space-y-6">
              <div>
                <label className="block text-[10px] font-black uppercase text-gray-400 mb-3 tracking-widest leading-none">Color de Marca</label>
                <input type="color" value={config.primaryColor} onChange={e => setConfig({...config, primaryColor: e.target.value})} className="h-12 w-full border-4 border-white rounded-xl cursor-pointer p-1 shadow-md outline-none bg-white"/>
              </div>
              <div className="grid grid-cols-2 gap-4">
                 <div>
                    <label className="block text-[10px] font-black uppercase text-gray-400 mb-3 tracking-widest leading-none">Moneda Principal</label>
                    <select value={config.currency} onChange={e => setConfig({...config, currency: e.target.value})} className="w-full p-3 bg-gray-50 rounded-xl border-none font-bold text-gray-700 focus:ring-2 focus:ring-blue-500">
                        <option value="" disabled>Seleccione...</option>
                        <option value="MXN">Pesos (MXN)</option>
                        <option value="USD">Dólares (USD)</option>
                    </select>
                 </div>
                 <div>
                    <label className="block text-[10px] font-black uppercase text-gray-400 mb-3 tracking-widest leading-none">Idioma Reportes</label>
                    <select value={config.language} onChange={e => setConfig({...config, language: e.target.value})} className="w-full p-3 bg-gray-50 rounded-xl border-none font-bold text-gray-700 focus:ring-2 focus:ring-blue-500">
                        <option value="es">Español</option>
                        <option value="en">Inglés</option>
                    </select>
                 </div>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'signatures' && (
          <div className="space-y-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-800">
              {config.signatures.length > 0 ? (
                config.signatures.map((s, i) => (
                  <div key={i} className="flex justify-between items-center p-6 bg-gray-50 rounded-[1.8rem] border-2 border-white shadow-sm hover:shadow-md transition-all">
                    <div>
                      <p className="font-black text-gray-900 uppercase text-xs tracking-wider leading-none mb-1.5">{s.name}</p>
                      <p className="text-[10px] text-gray-400 uppercase font-black tracking-widest">{getSigTitle(s.title, config.language)}</p>
                    </div>
                    <button onClick={() => handleRemoveSignature(i)} className="text-red-400 hover:text-red-600 transition-all p-3 bg-white rounded-xl shadow-sm hover:scale-110"><Trash2 size={20}/></button>
                  </div>
                ))
              ) : (
                <div className="col-span-2 p-6 text-center text-gray-400 text-xs italic">No hay firmas configuradas. Agregue una abajo.</div>
              )}
            </div>
            <div className="bg-gray-50 p-10 rounded-[2.5rem] space-y-6 shadow-inner">
              <h4 className="text-[11px] font-black uppercase tracking-[0.3em] text-gray-400 border-b border-gray-100 pb-6 mb-4 leading-none">Añadir Responsable</h4>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                   <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest ml-2">Cargo</label>
                   <select className="w-full border-2 border-white rounded-2xl p-4 text-xs font-black uppercase tracking-widest bg-white shadow-sm outline-none focus:ring-4 focus:ring-blue-50 transition-all text-gray-600" value={newSigTitle} onChange={e => setNewSigTitle(e.target.value)}>
                     <option value="">Seleccionar...</option>
                     {signatureTitlesList.map(t => <option key={t.id} value={t.id}>{config.language === 'en' ? t.labelEn : t.labelEs}</option>)}
                   </select>
                </div>
                <div className="space-y-2">
                   <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest ml-2">Nombre Completo</label>
                   <input className="w-full border-2 border-white rounded-2xl p-4 text-sm font-bold text-gray-800 shadow-sm outline-none focus:ring-4 focus:ring-blue-50 transition-all" value={newSigName} onChange={e => setNewSigName(e.target.value)} placeholder="Ej: John Doe" />
                </div>
              </div>
              <Button variant="secondary" onClick={handleAddSignature} className="w-full py-5 text-[11px] font-black uppercase tracking-[0.25em] border-none shadow-xl bg-white active:scale-95 text-indigo-600 hover:text-indigo-800"><Plus size={16} className="mr-2"/> Registrar Firma Oficial</Button>
            </div>
          </div>
        )}
        {activeTab === 'data' && (
            <div className="space-y-8">
                <div className="bg-blue-50 border border-blue-100 rounded-2xl p-8">
                    <div className="flex items-start gap-4">
                        <div className="p-3 bg-blue-100 text-blue-600 rounded-xl"><Database size={24}/></div>
                        <div>
                            <h3 className="text-lg font-bold text-gray-900 mb-2">Copia de Seguridad (Backup)</h3>
                            <p className="text-sm text-gray-600 mb-4">
                                Sus datos están seguros en la nube de Google. Sin embargo, puede descargar una copia completa de toda la información de su iglesia en formato JSON para su archivo personal o para migrar datos.
                            </p>
                            <Button onClick={handleFullBackup} disabled={loadingBackup} className="shadow-lg">
                                {loadingBackup ? 'Generando...' : <><Download size={18} className="mr-2"/> Descargar Respaldo Completo</>}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        )}

        {activeTab !== 'data' && (
            <div className="mt-16 pt-10 border-t border-gray-50 flex justify-end">
              <Button onClick={handleSave} color={config.primaryColor} className="px-20 py-6 uppercase font-black tracking-[0.3em] text-[11px] shadow-2xl rounded-3xl active:scale-95 transition-all"><Save size={16} className="mr-2"/> Guardar Cambios</Button>
            </div>
        )}
      </div>
    </div>
  );
};

const AuditView = ({ tenantId }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, "audit_logs"), where("tenantId", "==", tenantId));
    const unsub = onSnapshot(q, (snap) => {
      let data = snap.docs.map(d => ({id: d.id, ...d.data()}));
      data.sort((a, b) => {
          const timeA = a.timestamp?.seconds || 0;
          const timeB = b.timestamp?.seconds || 0;
          return timeB - timeA;
      });
      data = data.slice(0, 100);
      setLogs(data);
      setLoading(false);
    }, (error) => {
      console.error("Error audit:", error); 
      setLoading(false);
    });
    return () => unsub();
  }, [tenantId]);

  // EXPORTACIÓN LIMPIA AUDITORIA
  const handleExportAudit = () => {
    const cleanLogs = logs.map(l => ({
        "Fecha y Hora": formatFirestoreDate(l.timestamp),
        "Usuario": l.userEmail,
        "Acción Realizada": l.action,
        "Detalles Adicionales": l.details
    }));
    exportToCSV(cleanLogs, "Auditoria");
  };

  return (
    <div className="max-w-6xl mx-auto animate-fadeIn">
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-2xl font-bold text-gray-800">Registro de Auditoría</h2>
        <Button variant="secondary" onClick={handleExportAudit} className="text-xs">
           <Download size={14} className="mr-2"/> Exportar
        </Button>
      </div>
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 text-gray-500 border-b border-gray-100">
              <tr><th className="px-6 py-4 font-medium">Fecha / Hora</th><th className="px-6 py-4 font-medium">Usuario</th><th className="px-6 py-4 font-medium">Acción</th><th className="px-6 py-4 font-medium">Detalles</th></tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? <tr><td colSpan="4" className="p-8 text-center"><Loader className="animate-spin inline text-gray-400"/></td></tr> : logs.map(log => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-6 py-3 text-gray-500 whitespace-nowrap">{formatFirestoreDate(log.timestamp)}</td>
                    <td className="px-6 py-3 font-medium text-gray-800">{log.userEmail}</td>
                    <td className="px-6 py-3"><span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-bold">{log.action}</span></td>
                    <td className="px-6 py-3 text-gray-600 truncate max-w-xs" title={log.details}>{log.details}</td>
                  </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const BudgetsView = ({ tenantId, themeColor, user, currency }) => {
  const [budgets, setBudgets] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [selectedDept, setSelectedDept] = useState('');
  const [amount, setAmount] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());

  useEffect(() => {
    const unsubDepts = onSnapshot(query(collection(db, "departments"), where("tenantId", "==", tenantId)), (snap) => setDepartments(snap.docs.map(d => ({id: d.id, ...d.data()}))), (e) => console.warn(e));
    const unsubBudgets = onSnapshot(query(collection(db, "budgets"), where("tenantId", "==", tenantId)), (snap) => setBudgets(snap.docs.map(d => ({id: d.id, ...d.data()}))), (e) => console.warn(e));
    const unsubTx = onSnapshot(query(collection(db, "transactions"), where("tenantId", "==", tenantId), where("type", "==", "expense")), (snap) => setTransactions(snap.docs.map(d => d.data())), (e) => console.warn(e));
    return () => { unsubDepts(); unsubBudgets(); unsubTx(); };
  }, [tenantId]);

  const handleSaveBudget = async (e) => {
    e.preventDefault();
    if (!selectedDept || !amount) return;
    const deptName = departments.find(d => d.id === selectedDept)?.name;
    await addDoc(collection(db, "budgets"), { 
      tenantId, departmentId: selectedDept, departmentName: deptName, year: parseInt(year), amount: parseFloat(amount), createdAt: new Date() 
    });
    await logAudit(tenantId, user, "Crear Presupuesto", `Año: ${year}, Depto: ${deptName}, Monto: ${amount}`);
    setAmount(''); setSelectedDept('');
  };

  const handleDelete = async (id) => {
    if(window.confirm("¿Borrar?")) {
        try {
            await deleteDoc(doc(db, "budgets", id));
            await logAudit(tenantId, user, "Borrar Presupuesto", id);
        } catch (e) {
            console.error(e);
            window.alert("Error al eliminar: " + e.message);
        }
    }
  };
   
  const getSpent = (deptId, budgetYear) => transactions.filter(t => t.departmentId === deptId && new Date(t.date).getFullYear() === budgetYear).reduce((sum, t) => sum + Number(t.amount), 0);

  return (
    <div className="max-w-6xl mx-auto animate-fadeIn">
      <div className="flex justify-between items-center mb-8"><h2 className="text-2xl font-bold text-gray-800">Presupuestos Anuales</h2></div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 h-fit">
          <h3 className="font-bold text-gray-800 mb-4">Definir Presupuesto</h3>
          <form onSubmit={handleSaveBudget} className="space-y-4">
             <input type="number" className="w-full px-4 py-2 border rounded" value={year} onChange={e => setYear(e.target.value)} placeholder="Año" />
             <select className="w-full px-4 py-2 border rounded bg-white" value={selectedDept} onChange={e => setSelectedDept(e.target.value)} required><option value="">Depto...</option>{departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}</select>
             <input type="number" className="w-full px-4 py-2 border rounded" value={amount} onChange={e => setAmount(e.target.value)} required placeholder="Monto" />
             <Button type="submit" className="w-full" color={themeColor}>Guardar</Button>
          </form>
        </div>
        <div className="lg:col-span-2 space-y-4">
           {budgets.map(b => { 
              const spent = getSpent(b.departmentId, b.year); 
              const percent = Math.min((spent / b.amount) * 100, 100); 
              return (
                <div key={b.id} className="bg-white p-6 rounded-xl shadow-sm border">
                   <div className="flex justify-between mb-4">
                     <div><h4 className="font-bold">{b.departmentName}</h4><p className="text-xs text-gray-500">Año {b.year}</p></div>
                     <p className="font-bold">{formatMoney(b.amount, currency)}</p>
                   </div>
                   <div className="w-full bg-gray-100 rounded-full h-3 mb-2">
                      <div style={{ width: `${percent}%`, backgroundColor: percent > 80 ? '#ef4444' : themeColor }} className="h-full rounded-full"></div>
                   </div>
                   <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{formatMoney(spent, currency)} gastado ({percent.toFixed(1)}%)</span>
                      <button onClick={() => handleDelete(b.id)} className="text-red-500"><Trash2 size={16}/></button>
                   </div>
                </div>
              ); 
           })}
        </div>
      </div>
    </div>
  );
};

// --- NUEVA VISTA DE EQUIPO ---
const TeamView = ({ tenantId, user }) => {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '' });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const q = query(collection(db, "users"), where("tenantId", "==", tenantId));
    const unsub = onSnapshot(q, (snap) => {
      setTeam(snap.docs.map(d => ({id: d.id, ...d.data()})));
      setLoading(false);
    });
    return () => unsub();
  }, [tenantId]);

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (team.length >= 3) {
      window.alert("Límite alcanzado: Máximo 3 usuarios permitidos en este plan.");
      return;
    }
    setCreating(true);
    try {
      const secondaryApp = initializeApp(firebaseConfig, "Secondary");
      const secondaryAuth = getAuth(secondaryApp);
      
      const cred = await createUserWithEmailAndPassword(secondaryAuth, newUser.email, newUser.password);
      
      await setDoc(doc(db, "users", cred.user.uid), {
        email: newUser.email,
        name: newUser.name,
        role: 'admin', 
        tenantId: tenantId,
        createdAt: new Date(),
        createdBy: user.email
      });

      await signOut(secondaryAuth);
      deleteApp(secondaryApp).catch(e => console.log("Cleanup secondary app", e));
      
      await logAudit(tenantId, user, "Crear Usuario Equipo", newUser.email);
      setNewUser({ name: '', email: '', password: '' });
      window.alert("¡Usuario creado exitosamente! Ahora puede iniciar sesión.");
      
    } catch (e) {
      console.error(e);
      window.alert("Error creando usuario: " + e.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteUser = async (userId) => {
      if (userId === user.uid) {
          window.alert("No puedes eliminar tu propia cuenta desde aquí.");
          return;
      }
      if(window.confirm("¿Estás seguro de eliminar el acceso a este usuario?")) {
          try {
            await deleteDoc(doc(db, "users", userId));
            await logAudit(tenantId, user, "Eliminar Usuario Equipo", userId);
          } catch (e) {
            console.error(e);
            window.alert("Error al eliminar: " + e.message);
          }
      }
  }

  return (
    <div className="max-w-4xl mx-auto animate-fadeIn">
      <div className="flex justify-between items-center mb-8">
        <div>
            <h2 className="text-2xl font-bold text-gray-800">Gestión de Equipo</h2>
            <p className="text-gray-500 text-sm">Administra el acceso a la plataforma (Máx. 3 usuarios).</p>
        </div>
        <div className="bg-indigo-50 text-indigo-700 px-4 py-2 rounded-lg font-bold text-sm">
            {team.length} / 3 Usuarios
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 h-fit">
           <h3 className="font-bold mb-4 flex items-center"><UserPlus size={18} className="mr-2"/> Invitar Usuario</h3>
           <form onSubmit={handleAddUser} className="space-y-4">
             <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nombre</label>
                <input className="w-full border rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none" value={newUser.name} onChange={e => setNewUser({...newUser, name: e.target.value})} placeholder="Ej: Secretaria" required />
             </div>
             <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Correo</label>
                <input type="email" className="w-full border rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none" value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} placeholder="usuario@iglesia.com" required />
             </div>
             <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Contraseña</label>
                <input type="password" className="w-full border rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none" value={newUser.password} onChange={e => setNewUser({...newUser, password: e.target.value})} placeholder="******" required minLength={6} />
             </div>
             <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" disabled={creating || team.length >= 3}>
                {creating ? 'Creando...' : 'Crear Acceso'}
             </Button>
             {team.length >= 3 && <p className="text-xs text-red-500 text-center mt-2">Has alcanzado el límite de usuarios.</p>}
           </form>
        </div>

        <div className="lg:col-span-2 space-y-4">
           {loading ? <div className="text-center p-8"><Loader className="animate-spin inline text-gray-400"/></div> : team.map(u => (
               <div key={u.id} className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between group hover:border-indigo-200 transition-all">
                   <div className="flex items-center space-x-4">
                       <div className={`h-10 w-10 rounded-full flex items-center justify-center font-bold text-lg ${u.id === user.uid ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'}`}>
                           {u.name?.charAt(0).toUpperCase() || 'U'}
                       </div>
                       <div>
                           <h4 className="font-bold text-gray-900">{u.name} {u.id === user.uid && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded ml-2">Tú</span>}</h4>
                           <p className="text-sm text-gray-500">{u.email}</p>
                       </div>
                   </div>
                   {u.id !== user.uid && (
                       <button onClick={() => handleDeleteUser(u.id)} className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Revocar Acceso">
                           <Trash2 size={18}/>
                       </button>
                   )}
               </div>
           ))}
        </div>
      </div>
    </div>
  );
};

// --- PANELES PRINCIPALES ---

const LoginScreen = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [orgName, setOrgName] = useState('');
  const [mode, setMode] = useState('login'); 
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showMobileForm, setShowMobileForm] = useState(false);

  const handleAuth = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    try {
      if (mode === 'login') {
          await signInWithEmailAndPassword(auth, email, password);
      } else if (mode === 'register') {
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        const tenantId = `tenant_${cred.user.uid.slice(0, 8)}`;
        const trialEnd = new Date(); trialEnd.setDate(trialEnd.getDate() + 7);
        await setDoc(doc(db, "tenants", tenantId), { 
            name: orgName, ownerId: cred.user.uid, status: 'active', plan: 'demo', trialEndDate: trialEnd, createdAt: new Date(), 
            config: { 
              primaryColor: '#b4111a', 
              signatures: [{title:'Treasurer', name:''}, {title:'Senior Pastor', name:''}],
              currency: '', 
              language: 'es',
              logoUrl: "https://static.wixstatic.com/media/bcb13e_ee3c4ba2567445a48ec97d0793b7e1a9~mv2.png" // Logo por defecto
            }
        });
        await setDoc(doc(db, "users", cred.user.uid), { email, role: 'admin', tenantId, name: orgName });
        await addDoc(collection(db, "departments"), { tenantId, name: "General", createdAt: new Date() });
      } else if (mode === 'reset') {
          if(!email) throw new Error("Por favor ingresa tu correo electrónico.");
          await sendPasswordResetEmail(auth, email);
          window.alert("Se ha enviado un enlace para restablecer tu contraseña a tu correo electrónico.");
          setMode('login');
      }
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };

  return (
    <>
      <div className={`min-h-screen bg-white flex flex-col justify-center items-center p-8 text-center md:hidden relative overflow-hidden ${showMobileForm ? 'hidden' : 'flex'}`}>
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-cover bg-center" style={{backgroundImage: 'url("https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80")', filter: 'grayscale(10%)'}}></div>
          <div className="absolute inset-0 bg-gradient-to-b from-white/90 via-white/80 to-white/95"></div>
        </div>
        <div className="relative z-10 w-full flex flex-col items-center">
           <div className="mb-8"><BrandLogo className="w-36 h-36"/></div>
           <h1 className="text-4xl font-extrabold text-gray-900 mb-2 drop-shadow-md">MAYORDOMO FIEL</h1>
           <p className="text-[#b4111a] mb-6 font-bold drop-shadow-md">Mayordomía con Excelencia.</p>
           <div className="bg-white/80 backdrop-blur-sm p-5 rounded-2xl border border-gray-100 shadow-xl mb-8 w-full max-w-xs">
              <p className="text-gray-800 font-bold mb-4 text-sm">Gestión financiera integral para iglesias modernas.</p>
              <ul className="space-y-3 text-sm text-gray-700 text-left mx-auto w-fit">
                  <li className="flex items-center font-medium"><CheckCircle size={16} className="text-[#b4111a] mr-2"/> Diezmos y Ofrendas</li>
                  <li className="flex items-center font-medium"><CheckCircle size={16} className="text-[#b4111a] mr-2"/> Auditoría y Seguridad</li>
                  <li className="flex items-center font-medium"><CheckCircle size={16} className="text-[#b4111a] mr-2"/> Certificados Fiscales</li>
              </ul>
           </div>
           <Button color="#b4111a" onClick={() => { setMode('login'); setShowMobileForm(true); }} className="w-full mb-4 shadow-xl text-lg py-4">Iniciar Sesión</Button>
           <button onClick={() => { setMode('register'); setShowMobileForm(true); }} className="text-gray-600 border-2 border-gray-200 bg-white/50 backdrop-blur-sm rounded-xl py-4 w-full hover:bg-white transition-all font-bold">Prueba Gratis</button>
        </div>
      </div>
      <div className={`min-h-screen w-full font-sans bg-white overflow-hidden ${!showMobileForm ? 'hidden md:flex' : 'flex'}`}>
        <div className="hidden md:flex w-1/2 relative bg-slate-50 flex-col justify-center p-12 border-r">
          <div className="absolute inset-0 bg-cover bg-center" style={{backgroundImage: 'url("https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80")', filter: 'grayscale(10%)'}}></div>
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/70 to-transparent z-10"></div>
          <div className="relative z-20">
            <BrandLogo className="w-32 h-32 mb-6 object-contain"/>
            <h1 className="text-6xl font-extrabold text-gray-900 mb-4">MAYORDOMO FIEL</h1>
            <p className="text-2xl font-light text-[#b4111a] mb-6">Mayordomía con Excelencia.</p>
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl border border-white/50 shadow-lg max-w-md">
                <p className="text-gray-700 mb-4">Gestión financiera integral para iglesias modernas.</p>
                <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-center"><CheckCircle size={16} className="text-[#b4111a] mr-2"/> Diezmos y Ofrendas</li>
                    <li className="flex items-center"><CheckCircle size={16} className="text-[#b4111a] mr-2"/> Auditoría y Seguridad</li>
                    <li className="flex items-center"><CheckCircle size={16} className="text-[#b4111a] mr-2"/> Certificados Fiscales</li>
                </ul>
            </div>
          </div>
        </div>
        <div className="w-full md:w-1/2 bg-[#b4111a] flex flex-col justify-center items-center p-6 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-[#8a0e14] via-[#b4111a] to-[#5c080c] z-0"></div>
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 z-0"></div>
          <div className="w-full max-w-md bg-white/10 backdrop-blur-md p-10 rounded-3xl border border-white/20 relative z-10">
            <button onClick={() => setShowMobileForm(false)} className="md:hidden text-white mb-4 flex items-center"><ArrowLeft size={16}/> Volver</button>
            <h2 className="text-3xl font-bold text-white mb-6 text-center">
              {mode === 'login' ? 'Acceso' : mode === 'register' ? 'Registro' : 'Recuperar Contraseña'}
            </h2>
            {error && <div className="bg-red-500/50 text-white p-3 rounded mb-4 text-sm">{error}</div>}
            {mode === 'reset' && <p className="text-white text-sm mb-4 text-center">Ingresa tu correo para recibir un enlace de recuperación.</p>}
            <form onSubmit={handleAuth} className="space-y-4">
              {mode === 'register' && <input className="w-full p-3 rounded bg-white/90 text-gray-900 placeholder-gray-500" placeholder="Nombre Iglesia" value={orgName} onChange={e => setOrgName(e.target.value)} required />}
              <input className="w-full p-3 rounded bg-white/90 text-gray-900 placeholder-gray-500" placeholder="Correo Electrónico" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
              {mode !== 'reset' && <input className="w-full p-3 rounded bg-white/90 text-gray-900 placeholder-gray-500" placeholder="Contraseña" type="password" value={password} onChange={e => setPassword(e.target.value)} required />}
              <button type="submit" className="w-full py-3 bg-gray-900 text-white font-bold rounded hover:bg-black" disabled={loading}>
                 {loading ? '...' : (mode === 'login' ? 'Entrar' : mode === 'register' ? 'Crear Cuenta' : 'Enviar Enlace')}
              </button>
            </form>
            <div className="text-center pt-4 text-sm">
                {mode !== 'reset' && <button type="button" onClick={() => { setMode('reset'); setError(''); }} className="text-red-100 hover:text-white block w-full mb-2">¿Olvidaste tu contraseña?</button>}
                <button type="button" onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); }} className="text-white font-bold hover:underline">
                    {mode === 'login' ? '¿No tienes cuenta? Regístrate' : '¿Ya tienes cuenta? Inicia Sesión'}
                </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const SupportView = () => {
  return (
    <div className="max-w-2xl mx-auto animate-fadeIn pt-10">
      <div className="bg-white rounded-[2rem] shadow-xl border border-gray-100 p-12 text-center">
        <div className="inline-flex p-4 rounded-2xl bg-indigo-50 text-indigo-600 mb-6">
           <HelpCircle size={48} />
        </div>
        <h2 className="text-3xl font-extrabold text-gray-900 mb-4">Centro de Soporte</h2>
        <p className="text-gray-500 mb-8 text-lg">
          ¿Tienes alguna duda o inconveniente técnico? <br/>
          Nuestro equipo de soporte está listo para asistirte.
        </p>
        
        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 inline-block w-full">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Correo Electrónico Oficial</p>
            <a href="mailto:support@mayordomofiel.com" className="text-2xl font-bold text-indigo-600 hover:text-indigo-800 transition-colors break-all">
                support@mayordomofiel.com
            </a>
        </div>
      </div>
    </div>
  );
};

const TenantDashboard = ({ user, tenantProfile }) => {
  const [activeTab, setActiveTab] = useState('Dashboard');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [tenantData, setTenantData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isTxModalOpen, setIsTxModalOpen] = useState(false);
  const [editingTx, setEditingTx] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [balance, setBalance] = useState(0);
  const [totalIncome, setTotalIncome] = useState(0);
  const [totalExpense, setTotalExpense] = useState(0);
  const [isPricingOpen, setIsPricingOpen] = useState(false);
  const [incomeByDept, setIncomeByDept] = useState({});
  const [totalBudget, setTotalBudget] = useState(0);
  const [budgetSpent, setBudgetSpent] = useState(0);
  const [viewReceipt, setViewReceipt] = useState(null); 
  const [setupRequired, setSetupRequired] = useState(false);

  useEffect(() => {
    if (!tenantProfile?.tenantId) { setLoading(false); return; }
    const unsubTenant = onSnapshot(doc(db, "tenants", tenantProfile.tenantId), (docSnap) => { 
        if (docSnap.exists()) { 
            const data = { id: docSnap.id, ...docSnap.data() };
            setTenantData(data); 
            // NUEVO: Verificación estricta de configuración inicial
            if (!data.config?.currency) {
                setSetupRequired(true);
                setActiveTab('Settings');
            } else {
                setSetupRequired(false);
            }
            setLoading(false); 
        }
        else { setLoading(false); }
    });
    
    const qTx = query(collection(db, "transactions"), where("tenantId", "==", tenantProfile.tenantId));
    const unsubTx = onSnapshot(qTx, (snapshot) => {
      const txs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      txs.sort((a, b) => new Date(b.date) - new Date(a.date));
      setTransactions(txs);
      
      let inc = 0, exp = 0;
      let deptIncome = {};

      txs.forEach(t => {
        const val = Number(t.amount);
        if (t.type === 'income') {
          inc += val;
          const dName = t.departmentName || 'General';
          deptIncome[dName] = (deptIncome[dName] || 0) + val;
        } else {
          exp += val;
        }
      });
      
      setBalance(inc - exp);
      setTotalIncome(inc);
      setTotalExpense(exp);
      setIncomeByDept(deptIncome);
      setBudgetSpent(exp);
    });

    const currentYear = new Date().getFullYear();
    const qBudgets = query(collection(db, "budgets"), where("tenantId", "==", tenantProfile.tenantId));
    const unsubBudgets = onSnapshot(qBudgets, (snap) => {
        const budgets = snap.docs.map(d => d.data());
        const totalB = budgets.filter(b => b.year === currentYear).reduce((sum, b) => sum + Number(b.amount), 0);
        setTotalBudget(totalB);
    });

    return () => { unsubTenant(); unsubTx(); unsubBudgets(); };
  }, [tenantProfile]);

  const toggleReconciled = async (txId, currentStatus) => {
    await updateDoc(doc(db, "transactions", txId), { reconciled: !currentStatus });
  };
   
  const handleEditTx = (tx) => { setEditingTx(tx); setIsTxModalOpen(true); };
  const handleCloseTxModal = () => { setIsTxModalOpen(false); setEditingTx(null); };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader className="animate-spin text-red-800" size={32} /></div>;
  if (!tenantData) return <div className="min-h-screen flex flex-col items-center justify-center text-center p-6"><AlertCircle size={48} className="text-red-500 mb-4"/><h2 className="text-xl font-bold mb-2">No se encontró el perfil de la organización</h2><Button onClick={() => window.location.reload()}>Reintentar</Button></div>;

  // NUEVO: Verificación de suspensión por parte del admin
  if (tenantData.status === 'suspended') {
      return (
          <div className="min-h-screen flex flex-col items-center justify-center bg-gray-900 text-white p-6 text-center">
              <div className="p-6 bg-red-500/20 rounded-full mb-6 text-red-500 animate-pulse"><PauseCircle size={64} /></div>
              <h1 className="text-4xl font-bold mb-4">Servicio Suspendido</h1>
              <p className="text-gray-400 max-w-md mb-8">El acceso a esta cuenta ha sido suspendido temporalmente. Por favor, contacte con soporte para regularizar su situación.</p>
              <a href="mailto:support@mayordomofiel.com" className="bg-white text-gray-900 px-8 py-3 rounded-lg font-bold hover:bg-gray-200 transition-colors">Contactar Soporte</a>
              <button onClick={() => signOut(auth)} className="text-gray-500 hover:text-white underline mt-8 text-sm">Cerrar Sesión</button>
          </div>
      );
  }

  const isDemo = tenantData?.plan === 'demo';
  const daysLeft = isDemo && tenantData.trialEndDate ? Math.ceil((tenantData.trialEndDate.toDate() - new Date()) / (1000 * 60 * 60 * 24)) : 0;
  const isExpired = isDemo && daysLeft <= 0;
  const themeColor = tenantData.config?.primaryColor || '#b4111a';
  const currency = tenantData.config?.currency || 'MXN';

  if (isExpired) return (<div className="min-h-screen flex flex-col items-center justify-center bg-gray-900 text-white p-6 text-center"><AlertTriangle size={64} className="text-yellow-500 mb-6" /><h1 className="text-4xl font-bold mb-4">Periodo de Prueba Finalizado</h1><Button onClick={() => setIsPricingOpen(true)} className="w-full py-4 text-lg">Ver Planes y Precios</Button><button onClick={() => signOut(auth)} className="text-gray-500 hover:text-white underline mt-4">Cerrar Sesión</button><PricingModal isOpen={isPricingOpen} onClose={() => setIsPricingOpen(false)} user={user} tenantId={tenantData.id} tenantConfig={tenantData.config} /></div>);

  const SidebarItem = ({ icon: Icon, label, id }) => (<button onClick={() => { setActiveTab(id); setIsSidebarOpen(false); }} className={`w-full flex items-center space-x-3 px-6 py-3.5 transition-all duration-200 border-l-4 outline-none text-left mb-1 ${activeTab === id ? 'bg-white/10 shadow-inner' : 'border-transparent hover:bg-black/10'}`} style={{ borderLeftColor: activeTab === id ? '#ffffff' : 'transparent' }}><Icon size={20} color={activeTab === id ? '#ffffff' : '#ffffff'} className="opacity-90" /><span className={`text-sm font-medium ${activeTab === id ? 'text-white font-bold' : 'text-white opacity-80 group-hover:text-white'}`}>{label}</span></button>);

  return (
    <div className="flex h-screen w-full font-sans bg-gray-50 overflow-hidden text-gray-900">
      {isSidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setIsSidebarOpen(false)}></div>}
      <aside className={`fixed inset-y-0 left-0 z-50 w-72 transition-transform duration-300 lg:relative lg:translate-x-0 flex flex-col h-full shadow-2xl ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`} style={{ backgroundColor: themeColor }}>
        <div className="h-24 flex items-center px-6 border-b border-white/10"><div className="w-14 h-14 flex-shrink-0 mr-4 flex items-center justify-center"><ChurchLogo url={tenantData.config?.logoUrl} className="w-full h-full object-contain" /></div><span className="font-bold text-white text-sm leading-tight line-clamp-2">{tenantData.name}</span></div>
        <nav className="flex-1 py-6 px-0 overflow-y-auto">
            {/* Si falta configuración, ocultar menú */}
            {!setupRequired && (
                <>
                <SidebarItem id="Dashboard" icon={LayoutDashboard} label="Resumen" />
                <SidebarItem id="Entradas" icon={ArrowDownCircle} label="Entradas" />
                <SidebarItem id="Salidas" icon={ArrowUpCircle} label="Salidas" />
                <SidebarItem id="Budgets" icon={TrendingUp} label="Presupuestos" />
                <SidebarItem id="Reportes" icon={FileText} label="Reportes Oficiales" />
                <SidebarItem id="Departamentos" icon={PieChart} label="Departamentos" />
                <SidebarItem id="Miembros" icon={Users} label="Miembros" />
                <SidebarItem id="Audit" icon={Activity} label="Auditoría" />
                <SidebarItem id="Team" icon={Users} label="Equipo" />
                <SidebarItem id="Support" icon={HelpCircle} label="Soporte" />
                </>
            )}
            <SidebarItem id="Settings" icon={Settings} label="Configuración" />
        </nav>
        <div className="p-6 border-t border-white/10"><div className="flex items-center justify-center mb-4 opacity-50 space-x-2"><span className="text-xs text-white uppercase tracking-wider">Powered by</span>
        <BrandLogo className="w-5 h-5 object-contain" />
        </div><button onClick={() => signOut(auth)} className="flex items-center justify-center space-x-2 px-4 py-2 w-full text-white/90 hover:text-white hover:bg-white/10 rounded-lg transition-colors font-medium text-sm"><LogOut size={16} /><span>Cerrar Sesión</span></button></div>
      </aside>
      <main className="flex-1 flex flex-col h-full w-full relative">
        {isDemo && !isExpired && (<div className="bg-indigo-600 text-white text-xs font-bold text-center py-1 flex justify-center items-center sticky top-0 z-40"><Clock size={12} className="mr-2"/> MODO DE PRUEBA: Quedan {daysLeft} días. <button onClick={() => setIsPricingOpen(true)} className="ml-2 underline hover:text-indigo-200">Comprar Licencia</button></div>)}
        <header className="h-20 bg-white border-b border-gray-200 flex items-center justify-between px-6 lg:px-10 shadow-sm z-30 shrink-0">
          <div className="flex items-center"><button onClick={() => setIsSidebarOpen(true)} className="mr-4 lg:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg"><Menu size={24} /></button><h2 className="text-xl font-bold text-gray-800 hidden md:block">{activeTab}</h2></div>
          <div className="flex items-center space-x-4">
              {!setupRequired && <Button color={themeColor} onClick={() => setIsTxModalOpen(true)} className="hidden md:flex shadow-lg/30"><Plus size={18} className="mr-2"/> Nuevo Movimiento</Button>}
              <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-bold border border-gray-200 shadow-inner">{user.email.charAt(0).toUpperCase()}</div>
          </div>
        </header>
        <div className="flex-1 overflow-y-auto p-6 md:p-8 bg-slate-50">
          {setupRequired && activeTab !== 'Settings' && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6 flex items-center text-orange-800 animate-pulse">
                  <AlertCircle className="mr-2"/> <strong>IMPORTANTE:</strong> Por favor, selecciona la moneda principal de tu organización para continuar.
              </div>
          )}
          {activeTab === 'Settings' && <TenantSettings tenant={tenantData} onUpdateLocal={setTenantData} user={user} />}
          {!setupRequired && (
              <>
                {activeTab === 'Departamentos' && <DepartmentsView tenantId={tenantProfile.tenantId} themeColor={themeColor} user={user} />}
                {activeTab === 'Miembros' && <MembersView tenantId={tenantProfile.tenantId} themeColor={themeColor} tenantData={tenantData} user={user} />}
                {activeTab === 'Budgets' && <BudgetsView tenantId={tenantProfile.tenantId} themeColor={themeColor} user={user} currency={currency} />}
                {activeTab === 'Audit' && <AuditView tenantId={tenantProfile.tenantId} />}
                {activeTab === 'Team' && <TeamView tenantId={tenantProfile.tenantId} user={user} />}
                {activeTab === 'Entradas' && <TransactionsView tenantId={tenantProfile.tenantId} type="income" themeColor={themeColor} user={user} onEdit={handleEditTx} currency={currency} />}
                {activeTab === 'Salidas' && <TransactionsView tenantId={tenantProfile.tenantId} type="expense" themeColor={themeColor} user={user} onEdit={handleEditTx} currency={currency} />}
                {activeTab === 'Reportes' && <ReportsView tenantId={tenantProfile.tenantId} tenant={tenantData} user={user} />}
                {activeTab === 'Support' && <SupportView />}
                {activeTab === 'Dashboard' && (
                    <div className="animate-fadeIn max-w-7xl mx-auto space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 relative overflow-hidden"><div className="flex justify-between items-start mb-2"><h3 className="text-gray-500 text-xs font-bold uppercase">Saldo Total</h3><div className="p-2 bg-blue-50 rounded-lg text-blue-600"><Wallet size={20}/></div></div><p className="text-3xl font-bold text-gray-900">{formatMoney(balance, currency)}</p></div>
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"><div className="flex justify-between items-start mb-2"><h3 className="text-gray-500 text-xs font-bold uppercase">Entradas (Mes)</h3><div className="p-2 bg-green-50 rounded-lg text-green-600"><ArrowDownCircle size={20}/></div></div><p className="text-3xl font-bold text-green-700">{formatMoney(totalIncome, currency)}</p></div>
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"><div className="flex justify-between items-start mb-2"><h3 className="text-gray-500 text-xs font-bold uppercase">Salidas (Mes)</h3><div className="p-2 bg-red-50 rounded-lg text-red-600"><ArrowUpCircle size={20}/></div></div><p className="text-3xl font-bold text-red-700">{formatMoney(totalExpense, currency)}</p></div>
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"><div className="flex justify-between items-start mb-2"><h3 className="text-gray-500 text-xs font-bold uppercase">Presupuesto Anual</h3><div className="p-2 bg-purple-50 rounded-lg text-purple-600"><Target size={20}/></div></div><p className="text-xl font-bold text-gray-900">{formatMoney(totalBudget, currency)}</p><div className="w-full bg-gray-100 rounded-full h-2 mt-2"><div style={{ width: `${Math.min((budgetSpent/totalBudget)*100, 100)}%` }} className="h-full rounded-full bg-purple-600"></div></div><p className="text-xs text-gray-500 mt-1">{((budgetSpent/totalBudget)*100 || 0).toFixed(0)}% Ejecutado</p></div>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"><h3 className="font-bold text-gray-800 mb-6 flex items-center"><PieChart className="mr-2" size={18}/> Desglose de Ingresos</h3><div className="space-y-4">{Object.keys(incomeByDept).length === 0 ? <p className="text-gray-400 text-sm">Sin datos aún.</p> : null}{Object.entries(incomeByDept).map(([name, amount], idx) => (<div key={idx} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg"><div className="flex items-center space-x-3"><div className="w-2 h-10 rounded bg-green-500"></div><span className="font-medium text-gray-700">{name}</span></div><span className="font-bold text-gray-900">{formatMoney(amount, currency)}</span></div>))}</div></div>
                        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden min-h-[400px]"><div className="p-6 border-b border-gray-100 flex justify-between items-center bg-white sticky top-0"><h3 className="font-bold text-lg text-gray-800">Últimos Movimientos</h3><div className="flex space-x-2"><Button variant="secondary" onClick={() => exportToCSV(transactions, "Tx")} className="text-xs hidden sm:flex"><Download size={14} className="mr-2"/> CSV</Button><Button onClick={() => setIsTxModalOpen(true)} className="md:hidden" color={themeColor}><Plus size={16}/></Button></div></div><div className="p-0 overflow-x-auto"><table className="w-full text-left text-sm"><thead className="bg-gray-50 text-gray-500 border-b border-gray-200 uppercase text-xs font-bold tracking-wider"><tr><th className="px-6 py-4 w-10">OK</th><th className="px-6 py-4">Fecha</th><th className="px-6 py-4">Descripción</th><th className="px-6 py-4">Depto.</th><th className="px-6 py-4">Info</th><th className="px-6 py-4 text-right">Monto</th><th className="px-6 py-4 w-10"></th></tr></thead><tbody className="divide-y divide-gray-100">{transactions.slice(0, 10).map(tx => (<tr key={tx.id} className="hover:bg-gray-50 transition-colors"><td className="px-6 py-4"><button onClick={() => toggleReconciled(tx.id, tx.reconciled)} className={`transition-colors ${tx.reconciled ? 'text-green-500' : 'text-gray-300 hover:text-gray-400'}`}><CheckCircle size={20} className={tx.reconciled ? "fill-current" : ""} /></button></td><td className="px-6 py-4 text-gray-600 font-medium whitespace-nowrap">{tx.date}</td><td className="px-6 py-4 font-semibold text-gray-800">{tx.description}</td><td className="px-6 py-4"><span className="bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full text-xs font-bold">{tx.departmentName || '-'}</span></td><td className="px-6 py-4"><div className="flex space-x-2">{tx.memberName && <span className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded flex items-center"><Users size={12} className="mr-1"/> {tx.memberName}</span>}{tx.receiptBase64 && (<button onClick={() => setViewReceipt(tx.receiptBase64)} className="text-gray-400 hover:text-blue-600" title="Ver Recibo"><Paperclip size={16}/></button>)}</div></td><td className={`px-6 py-4 text-right font-bold text-base ${tx.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>{tx.type === 'income' ? '+' : '-'}{formatMoney(tx.amount, currency)}</td><td className="px-6 py-4"><button onClick={() => handleEditTx(tx)} className="text-gray-400 hover:text-blue-600 transition-colors p-1" title="Editar"><Pencil size={16}/></button></td></tr>))}</tbody></table></div></div>
                    </div>
                    </div>
                )}
              </>
          )}
        </div>
      </main>
      <TransactionModal isOpen={isTxModalOpen} onClose={handleCloseTxModal} tenantId={tenantProfile.tenantId} themeColor={themeColor} user={user} initialData={editingTx} />
      <ReceiptViewer isOpen={!!viewReceipt} onClose={() => setViewReceipt(null)} imgUrl={viewReceipt} />
      <PricingModal isOpen={isPricingOpen} onClose={() => setIsPricingOpen(false)} user={user} tenantId={tenantData.id} tenantConfig={tenantData.config} />
    </div>
  );
};

const SuperAdminDashboard = ({ user, myTenantProfile }) => {
  const [activeTab, setActiveTab] = useState('licenses');
  const [viewMode, setViewMode] = useState('admin');
  const [generatedKey, setGeneratedKey] = useState('');
  const [config, setConfig] = useState({ 
    prices: { small: 25, medium: 50, large: 99 }, 
    prices_mxn: { small: 500, medium: 1000, large: 2000 },
    links: { small:'', medium:'', large:'' } 
  });
  const [tenants, setTenants] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [userMap, setUserMap] = useState({}); // Nuevo estado para mapa de usuarios

  useEffect(() => {
    onSnapshot(doc(db, "system", "config"), (doc) => { if(doc.exists()) setConfig(doc.data()); });
    
    // Escuchar Tenants para gestión
    const qTenants = query(collection(db, "tenants"));
    onSnapshot(qTenants, (snap) => {
        setTenants(snap.docs.map(d => ({id: d.id, ...d.data()})));
    });

    // Escuchar Usuarios para obtener correos
    const qUsers = query(collection(db, "users"));
    onSnapshot(qUsers, (snap) => {
        const map = {};
        snap.docs.forEach(d => {
            const u = d.data();
            map[d.id] = u.email; // Mapeamos ID de usuario -> Email
        });
        setUserMap(map);
    });

    // Escuchar Licencias
    const qLicenses = query(collection(db, "licenses"), orderBy("generatedAt", "desc"));
    onSnapshot(qLicenses, (snap) => {
        setLicenses(snap.docs.map(d => ({id: d.id, ...d.data()})));
    });
  }, []);

  const handleGenerateKey = async () => {
    const key = `FIDS-${Math.random().toString(36).substr(2, 4).toUpperCase()}-${Math.random().toString(36).substr(2, 4).toUpperCase()}`;
    await addDoc(collection(db, "licenses"), { key, status: 'active', generatedAt: new Date() });
    setGeneratedKey(key);
  };

  const handleSaveConfig = async () => {
    await setDoc(doc(db, "system", "config"), config, { merge: true });
    window.alert("Configuración de Ventas Actualizada");
  };

  const handleToggleStatus = async (tenantId, currentStatus) => {
      const newStatus = currentStatus === 'suspended' ? 'active' : 'suspended';
      if(window.confirm(`¿Estás seguro de ${newStatus === 'suspended' ? 'SUSPENDER' : 'ACTIVAR'} esta organización?`)) {
          await updateDoc(doc(db, "tenants", tenantId), { status: newStatus });
      }
  };

  const handleDeleteTenant = async (tenantId) => {
      if(window.confirm("PELIGRO: ¿Borrar permanentemente esta organización? No se puede deshacer.")) {
          try {
            await deleteDoc(doc(db, "tenants", tenantId));
            window.alert("Organización eliminada.");
          } catch (e) {
            console.error(e);
            window.alert("Error al eliminar: " + e.message);
          }
      }
  };

  const handleDeleteLicense = async (licenseId) => {
      if(window.confirm("¿Borrar esta licencia?")) {
          try {
            await deleteDoc(doc(db, "licenses", licenseId));
          } catch (e) {
            console.error(e);
            window.alert("Error al eliminar licencia: " + e.message);
          }
      }
  };

  if (viewMode === 'my_church') return (<div className="relative"><div className="bg-gray-900 text-white p-2 flex justify-between"><span className="font-bold flex items-center"><Shield size={16} className="mr-2"/> MODO SUPER ADMIN</span><button onClick={() => setViewMode('admin')} className="bg-indigo-600 px-3 py-1 rounded text-xs font-bold">Volver al Panel Maestro</button></div><TenantDashboard user={user} tenantProfile={myTenantProfile} /></div>);

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 font-sans">
      <header className="bg-slate-950 border-b border-slate-800 p-4 flex justify-between items-center shadow-lg">
        <div className="flex items-center space-x-3"><BrandLogo className="w-8 h-8 object-contain" /><h1 className="text-lg font-bold tracking-wide text-gray-200">MASTER ADMIN</h1></div>
        <div className="flex items-center space-x-4"><span className="text-xs text-slate-400">{user.email}</span><Button variant="dark" onClick={() => setViewMode('my_church')} className="text-xs"><Briefcase size={14} className="mr-2" /> Mi Iglesia</Button><button onClick={() => signOut(auth)} className="text-xs bg-red-900/30 text-red-400 border border-red-900 px-3 py-1.5 rounded">Salir</button></div>
      </header>
      <div className="container mx-auto p-8">
        <div className="flex space-x-4 mb-6 border-b border-slate-700 overflow-x-auto">
            <button onClick={() => setActiveTab('licenses')} className={`pb-3 px-4 whitespace-nowrap ${activeTab === 'licenses' ? 'border-b-2 border-indigo-500 text-white' : 'text-slate-400'}`}>Generador de Licencias</button>
            <button onClick={() => setActiveTab('tenants')} className={`pb-3 px-4 whitespace-nowrap ${activeTab === 'tenants' ? 'border-b-2 border-indigo-500 text-white' : 'text-slate-400'}`}>Gestión de Iglesias</button>
            <button onClick={() => setActiveTab('sales')} className={`pb-3 px-4 whitespace-nowrap ${activeTab === 'sales' ? 'border-b-2 border-indigo-500 text-white' : 'text-slate-400'}`}>Ventas y Precios</button>
        </div>
        
        {activeTab === 'licenses' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="bg-slate-800 p-8 rounded-xl border border-slate-700 h-fit">
                    <h2 className="text-xl font-bold mb-4">Generar Nueva Clave</h2>
                    <Button onClick={handleGenerateKey} className="w-full py-4 text-lg mb-6">Generar Clave</Button>
                    {generatedKey && (<div className="bg-green-900/30 border border-green-500/50 p-4 rounded-lg text-center"><p className="text-green-400 text-sm font-bold mb-1">CLAVE GENERADA:</p><p className="text-3xl font-mono text-white tracking-widest select-all">{generatedKey}</p></div>)}
                </div>
                <div className="lg:col-span-2 bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                    <div className="p-4 bg-slate-900 border-b border-slate-700 font-bold">Historial de Licencias</div>
                    <div className="max-h-[500px] overflow-y-auto">
                        <table className="w-full text-left text-sm text-slate-300">
                            <thead className="bg-slate-900/50 text-slate-500"><tr><th className="p-3">Clave</th><th className="p-3">Estado</th><th className="p-3">Usada Por</th><th className="p-3 text-right">Acción</th></tr></thead>
                            <tbody className="divide-y divide-slate-700">
                                {licenses.map(lic => (
                                    <tr key={lic.id}>
                                        <td className="p-3 font-mono">{lic.key}</td>
                                        <td className="p-3"><span className={`px-2 py-1 rounded text-xs font-bold ${lic.status === 'redeemed' ? 'bg-green-900 text-green-300' : 'bg-blue-900 text-blue-300'}`}>{lic.status}</span></td>
                                        <td className="p-3 text-xs">{lic.usedBy || '-'}</td>
                                        <td className="p-3 text-right"><button onClick={() => handleDeleteLicense(lic.id)} className="text-red-400 hover:text-red-300"><Trash2 size={16}/></button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        )}

        {activeTab === 'tenants' && (
            <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                <div className="p-6 border-b border-slate-700 flex justify-between items-center">
                    <h2 className="text-xl font-bold">Iglesias Registradas</h2>
                    <span className="bg-indigo-900 text-indigo-300 px-3 py-1 rounded-full text-sm font-bold">{tenants.length} Total</span>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm text-slate-300">
                        <thead className="bg-slate-900 text-slate-400 uppercase text-xs">
                            <tr>
                                <th className="p-4">Nombre</th>
                                <th className="p-4">Correo Admin</th>
                                <th className="p-4">Plan</th>
                                <th className="p-4">Estado</th>
                                <th className="p-4 text-center">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700">
                            {tenants.map(t => (
                                <tr key={t.id} className="hover:bg-slate-700/50 transition-colors">
                                    <td className="p-4 font-bold text-white">
                                        {t.name}
                                        <div className="text-[10px] text-slate-600 font-mono mt-1">{t.id}</div>
                                    </td>
                                    <td className="p-4 text-indigo-300 font-medium">
                                        {userMap[t.ownerId] || 'No asignado'}
                                    </td>
                                    <td className="p-4"><span className="bg-slate-700 px-2 py-1 rounded text-xs uppercase">{t.plan}</span></td>
                                    <td className="p-4">
                                        {t.status === 'suspended' ? 
                                            <span className="text-red-400 flex items-center text-xs font-bold"><PauseCircle size={14} className="mr-1"/> SUSPENDIDO</span> : 
                                            <span className="text-green-400 flex items-center text-xs font-bold"><PlayCircle size={14} className="mr-1"/> ACTIVO</span>
                                        }
                                    </td>
                                    <td className="p-4 flex justify-center gap-3">
                                        <button onClick={() => handleToggleStatus(t.id, t.status)} className={`p-2 rounded hover:bg-slate-600 ${t.status === 'suspended' ? 'text-green-400' : 'text-yellow-400'}`} title={t.status === 'suspended' ? "Reactivar" : "Suspender"}>
                                            {t.status === 'suspended' ? <PlayCircle size={18}/> : <PauseCircle size={18}/>}
                                        </button>
                                        <button onClick={() => handleDeleteTenant(t.id)} className="p-2 rounded hover:bg-slate-600 text-red-500" title="Eliminar Definitivamente">
                                            <Trash2 size={18}/>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        )}

        {activeTab === 'sales' && (
          <div className="bg-slate-800 p-8 rounded-xl border border-slate-700">
             <h2 className="text-xl font-bold mb-6">Configuración de Precios</h2>
             <div className="mb-6 bg-slate-900 p-4 rounded border border-slate-700">
               <h3 className="text-indigo-400 font-bold border-b border-slate-700 pb-2 mb-4">Precios en Dólares (USD)</h3>
               <div className="grid md:grid-cols-3 gap-6">
                 {['small', 'medium', 'large'].map(plan => (
                    <div key={'usd'+plan}><label className="block text-xs text-slate-500 mb-1 capitalize">Plan {plan}</label><input type="number" className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white" value={config.prices?.[plan]} onChange={e => setConfig({...config, prices: {...config.prices, [plan]: e.target.value}})} /></div>
                 ))}
               </div>
             </div>
             <div className="mb-6 bg-slate-900 p-4 rounded border border-slate-700">
               <h3 className="text-green-400 font-bold border-b border-slate-700 pb-2 mb-4">Precios en Pesos (MXN)</h3>
               <div className="grid md:grid-cols-3 gap-6">
                 {['small', 'medium', 'large'].map(plan => (
                    <div key={'mxn'+plan}><label className="block text-xs text-slate-500 mb-1 capitalize">Plan {plan}</label><input type="number" className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white" value={config.prices_mxn?.[plan]} onChange={e => setConfig({...config, prices_mxn: {...(config.prices_mxn || {}), [plan]: e.target.value}})} /></div>
                 ))}
               </div>
             </div>
             <div className="mb-6 bg-slate-900 p-4 rounded border border-slate-700">
                <h3 className="text-gray-400 font-bold border-b border-slate-700 pb-2 mb-4">Links de Pago</h3>
                <div className="grid md:grid-cols-3 gap-6">
                    {['small', 'medium', 'large'].map(plan => (
                        <div key={'link'+plan}><label className="block text-xs text-slate-500 mb-1 capitalize">Link {plan}</label><input type="text" className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white text-xs" value={config.links?.[plan]} onChange={e => setConfig({...config, links: {...config.links, [plan]: e.target.value}})} placeholder="https://..." /></div>
                    ))}
                </div>
             </div>
             <div className="mt-6 flex justify-end"><Button onClick={handleSaveConfig}>Guardar Configuración Global</Button></div>
          </div>
        )}
      </div>
    </div>
  );
};

// --- COMPONENTE ROOT ---
export default function App() {
  const [user, setUser] = useState(null);
  const [tenantProfile, setTenantProfile] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const logoUrl = "https://static.wixstatic.com/media/bcb13e_ee3c4ba2567445a48ec97d0793b7e1a9~mv2.png";
    
    const updateFavicon = () => {
      const existingIcons = document.querySelectorAll("link[rel*='icon']");
      existingIcons.forEach(el => el.remove());

      const link = document.createElement('link');
      link.type = 'image/png';
      link.rel = 'icon';
      link.href = logoUrl;
      document.head.appendChild(link);

      const appleLink = document.createElement('link');
      appleLink.rel = 'apple-touch-icon';
      appleLink.href = logoUrl;
      document.head.appendChild(appleLink);
    };

    updateFavicon();
    document.title = "Mayordomo Fiel - Plataforma Financiera";
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        onSnapshot(doc(db, "users", currentUser.uid), (docSnap) => {
          let profile = docSnap.exists() ? docSnap.data() : null;
          if (currentUser.email === 'carlosmedina@wpartners.org') profile = { ...profile, role: 'super_admin', isSuperAdmin: true };
          setTenantProfile(profile);
          setAuthLoading(false);
        });
      } else { setTenantProfile(null); setAuthLoading(false); }
    });
    return () => unsubscribe();
  }, []);

  if (authLoading) return <div className="min-h-screen flex items-center justify-center"><Loader className="animate-spin" /></div>;
  if (!user) return <LoginScreen />;
  if (tenantProfile?.isSuperAdmin) return <SuperAdminDashboard user={user} myTenantProfile={tenantProfile} />;
  if (tenantProfile) return <TenantDashboard user={user} tenantProfile={tenantProfile} />;
  return <div className="p-10 text-center">Cargando...</div>;
}